import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authJsonHeaders } from '../utils/authHeaders';
import './NHPage.css';
import './CgmFilterToolbar.css';
import Invoice from './Invoice';
import {
  enrichInvoiceClientFromRegistry,
  enrichInvoiceClientFromApi,
  buildClientRegistryFromApiClients,
  normalizeInvoiceClientData,
  autoExportNotePdf,
} from './invoicePrint';
import {
  fetchNhDbMaxSeqForDate,
  noteNumeroKey,
  nextNhAutoNumeroAfterMerge,
  nextNhAutoNumeroCompact,
  parseNhCompactNumero,
  resolveUniqueNhNumero,
} from '../utils/nhNumero';
import { getClientNomComplet } from '../utils/clientNom';

const NH_OVERVIEW_STORAGE_KEY = 'gestionCAIS_nh_notes_overview';
const NH_MATRICULE_STORAGE_KEY = 'gestionCAIS_nh_selected_matricule';
const NH_ACTIVE_MENU_STORAGE_KEY = 'gestionCAIS_nh_active_menu';
const NH_ALLOWED_MATRICULES = new Set(['1408941H', '1835448D']);
const NH_MATRICULE_CGM1 = '1408941H';
const NH_MATRICULE_CGM2 = '1835448D';
const NH_ALLOWED_MENUS = new Set(['add', 'overview', 'search']);

/** Matricule NH selon le cabinet actif (CGM1 → 1408941H, CGM2 → 1835448D). */
function resolveMatriculeForActiveCabinet(cabinet) {
  if (!cabinet) return NH_MATRICULE_CGM1;
  const vat = String(cabinet.vat_code || cabinet.vatCode || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '');
  if (vat.startsWith('1835448') || vat === NH_MATRICULE_CGM2) return NH_MATRICULE_CGM2;
  if (vat.startsWith('1408941') || vat === NH_MATRICULE_CGM1) return NH_MATRICULE_CGM1;
  if (Number(cabinet.is_default) === 1) return NH_MATRICULE_CGM1;
  const code = String(cabinet.code || '').toUpperCase();
  const name = String(cabinet.name || '').toUpperCase();
  if (code.includes('CGM1') || name.includes('CGM1')) return NH_MATRICULE_CGM1;
  if (code.includes('CGM2') || name.includes('CGM2')) return NH_MATRICULE_CGM2;
  return NH_MATRICULE_CGM2;
}

/** Libellés courts pour la grille « période à facturer » (Ajouter une note). */
const NH_MONTH_LABELS_SHORT = [
  'Jan.', 'Fév.', 'Mars', 'Avr.', 'Mai', 'Juin',
  'Juil.', 'Août', 'Sept.', 'Oct.', 'Nov.', 'Déc.'
];

const NH_MONTH_LABELS_FULL = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

/** Dernier jour du mois (YYYY-MM-DD), courant pour une NH de fin de période. */
function nhLastDayIsoOfMonth(year, monthIndex0Based) {
  const d = new Date(year, monthIndex0Based + 1, 0);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

function nhParseYearMonthFromIsoDate(iso) {
  if (!iso || typeof iso !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-\d{2}/.exec(iso.trim());
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10) - 1;
  if (!Number.isFinite(y) || mo < 0 || mo > 11) return null;
  return { year: y, monthIndex: mo };
}

/** Données fiscales par défaut (années disponibles au premier chargement). */
const NH_INITIAL_FISCAL_DATA = {
  2021: { retenue: 0, timbre: 0.600, tva0: 13, tva19: 5 },
  2022: { retenue: 3, timbre: 0.600, tva0: 13, tva19: 0 },
  2023: { retenue: 3, timbre: 1.000, tva0: 0, tva19: 19 },
  2024: { retenue: 3, timbre: 1.000, tva0: 0, tva19: 19 },
  2025: { retenue: 3, timbre: 1.000, tva0: 0, tva19: 19 },
  2026: { retenue: 3, timbre: 1.000, tva0: 0, tva19: 19 }
};

const NH_FALLBACK_FISCAL_PARAMS = { retenue: 3, timbre: 1.0, tva0: 0, tva19: 19 };

/**
 * Au Nouvel An (horloge PC) : ajoute l’année civile dans la liste Vue d’ensemble / Ajouter,
 * en recopiant les paramètres fiscaux de la dernière année connue. Pas de notes → liste vide.
 */
function ensureNhFiscalYearsThroughCalendar(fiscalDataObj) {
  const calendar = new Date().getFullYear();
  const next = { ...(fiscalDataObj || {}) };
  if (next[calendar]) return next;

  const keys = Object.keys(next)
    .map(Number)
    .filter((k) => Number.isFinite(k) && k >= 2000 && k <= 2100);
  const notFuture = keys.filter((k) => k <= calendar);
  const srcYear = notFuture.length
    ? Math.max(...notFuture)
    : keys.length
      ? Math.max(...keys)
      : null;
  const src =
    srcYear != null && next[srcYear]
      ? next[srcYear]
      : NH_FALLBACK_FISCAL_PARAMS;
  next[calendar] = { ...src };
  return next;
}

function nhTodayIsoLocal() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Année civile courante si présente dans les paramètres fiscaux, sinon la plus proche inférieure (ou la plus petite année dispo). */
function nhDefaultSelectedYearForFiscal(fiscalDataObj) {
  const y = new Date().getFullYear();
  const keys = Object.keys(fiscalDataObj || {}).map(Number);
  if (!keys.length) return y;
  if (keys.includes(y)) return y;
  const notFuture = keys.filter((k) => k <= y);
  if (notFuture.length) return Math.max(...notFuture);
  return Math.min(...keys);
}

/**
 * Date initiale du formulaire « Ajouter une note » : jour courant si l’année fiscale affichée
 * est l’année civile ; sinon dernier jour du mois courant dans l’année fiscale retenue (cohérence année / mois).
 */
function nhDefaultDateNHForAddForm(fiscalDataObj) {
  const d = new Date();
  const yCal = d.getFullYear();
  const ySel = nhDefaultSelectedYearForFiscal(fiscalDataObj);
  const m0 = d.getMonth();
  if (yCal === ySel) return nhTodayIsoLocal();
  return nhLastDayIsoOfMonth(ySel, m0);
}

function getInitialNhUiState(routerState) {
  if (routerState?.pickMatricule) {
    return { matricule: null, menu: 'add' };
  }
  if (typeof window === 'undefined') {
    return { matricule: null, menu: 'add' };
  }
  try {
    const matRaw = window.localStorage.getItem(NH_MATRICULE_STORAGE_KEY);
    const matricule = matRaw && NH_ALLOWED_MATRICULES.has(matRaw) ? matRaw : null;
    let menu = 'add';
    if (matricule) {
      const menuRaw = window.localStorage.getItem(NH_ACTIVE_MENU_STORAGE_KEY);
      if (menuRaw && NH_ALLOWED_MENUS.has(menuRaw)) {
        menu = menuRaw;
      }
    }
    return { matricule, menu };
  } catch {
    return { matricule: null, menu: 'add' };
  }
}

function loadNhOverviewFromStorage() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(NH_OVERVIEW_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((row) => row && typeof row === 'object' && row.id != null);
  } catch {
    return [];
  }
}

/** Partie affichée après le préfixe « NH » dans le champ (sans redoubler NH) */
function nhBodyWithoutPrefix(fullOrBody) {
  return String(fullOrBody || '')
    .trim()
    .replace(/^NH/gi, '');
}

/** Clé de tri numérique pour numéros NH compacts (NHMMYYYYseq). */
function nhOverviewNumeroSortTuple(numero) {
  const p = parseNhCompactNumero(numero);
  if (!p) return null;
  return {
    yyyy: parseInt(p.yyyy, 10) || 0,
    mm: parseInt(p.mm, 10) || 0,
    seq: p.seq || 0,
  };
}

/** Rang type période pour grouper : mensuel → trimestriel → semestriel → annuel. */
function overviewNotePeriodRank(note) {
  const designation = String(note?.designation || '').trim();
  const d = designation.toLowerCase();
  if (/trimestre|trimestriel/.test(d)) return 1;
  if (/semestre|semestriel/.test(d)) return 2;
  if (/^assistance comptable\s+\d{4}$/i.test(designation)) return 3;
  if (/\bannuel\b|ann[ée]e\s+\d{4}/i.test(d) && !/\bmois\b/.test(d)) return 3;
  return 0; // mensuel (Mois …) ou défaut
}

/**
 * Vue d'ensemble : mois décroissant (12 → 01), puis type (mensuel, trimestriel…),
 * puis numéro NH / date.
 */
function sortOverviewNotesDesc(notes) {
  return [...(notes || [])].sort((a, b) => {
    const ya = overviewNoteYear(a);
    const yb = overviewNoteYear(b);
    if (ya != null && yb != null && ya !== yb) return yb - ya; // année décroissante

    const ma = overviewNoteMonth(a);
    const mb = overviewNoteMonth(b);
    // Mois 12 → 11 → … → 01 (groupes)
    if (ma != null && mb != null && ma !== mb) return mb - ma;

    const pa = overviewNotePeriodRank(a);
    const pb = overviewNotePeriodRank(b);
    if (pa !== pb) return pa - pb; // mensuel, puis trimestriel, puis semestriel, puis annuel

    const ta = nhOverviewNumeroSortTuple(a?.numero);
    const tb = nhOverviewNumeroSortTuple(b?.numero);
    if (ta && tb) {
      if (ta.seq !== tb.seq) return tb.seq - ta.seq;
    } else {
      const byNumero = String(b?.numero || '').localeCompare(
        String(a?.numero || ''),
        undefined,
        { numeric: true, sensitivity: 'base' }
      );
      if (byNumero !== 0) return byNumero;
    }
    const byDate = String(b?.date || b?.createdAt || '').localeCompare(
      String(a?.date || a?.createdAt || '')
    );
    if (byDate !== 0) return byDate;
    return (Number(b.id) || 0) - (Number(a.id) || 0);
  });
}

/** Date ISO (YYYY-MM-DD) de la dernière note NH (date facture), ex. 31/05/2026. */
function nhLastNoteCreationDateIso(notes, matriculeComptable) {
  let rows = notes || [];
  if (matriculeComptable) {
    rows = rows.filter(
      (item) => !item.matriculeComptable || item.matriculeComptable === matriculeComptable
    );
  }
  let maxIso = '';
  for (const note of rows) {
    const iso =
      parseNhIsoDate(note?.date, note?.numero) ||
      parseNhIsoDate(note?.createdAt);
    if (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) && iso > maxIso) {
      maxIso = iso;
    }
  }
  return maxIso || null;
}

/** True si la date est valide pour une nouvelle note (>= dernière facture, même jour OK). */
function nhIsDateAllowedForNewNote(iso, lastNoteCreationDateIso) {
  const d = String(iso || '').slice(0, 10);
  if (!d || !lastNoteCreationDateIso) return true;
  return d >= lastNoteCreationDateIso;
}

/** Année + mois depuis le numéro NH compact (NHMMYYYYseq) si la date stockée est corrompue. */
function dateIsoFromNhNumero(numero) {
  const p = parseNhCompactNumero(numero);
  if (!p) return '';
  const today = new Date();
  const dd =
    today.getFullYear() === parseInt(p.yyyy, 10) &&
      String(today.getMonth() + 1).padStart(2, '0') === p.mm
      ? String(today.getDate()).padStart(2, '0')
      : '01';
  return `${p.yyyy}-${p.mm}-${dd}`;
}

/** Année civile d'une note (vue d'ensemble). */
function overviewNoteYear(note) {
  const iso = parseNhIsoDate(note?.date, note?.numero);
  if (iso && iso.length >= 4) {
    const y = parseInt(iso.slice(0, 4), 10);
    if (Number.isFinite(y) && y >= 2000) return y;
  }
  const fromNumero = parseNhCompactNumero(note?.numero);
  if (fromNumero?.yyyy) {
    const y = parseInt(fromNumero.yyyy, 10);
    if (Number.isFinite(y) && y >= 2000) return y;
  }
  return null;
}

/** Mois (0-11) d'une note (vue d'ensemble). */
function overviewNoteMonth(note) {
  const iso = parseNhIsoDate(note?.date, note?.numero);
  if (iso && iso.length >= 7) {
    const m = parseInt(iso.slice(5, 7), 10) - 1;
    if (Number.isFinite(m) && m >= 0 && m <= 11) return m;
  }
  const fromNumero = parseNhCompactNumero(note?.numero);
  if (fromNumero?.mm) {
    const m = parseInt(fromNumero.mm, 10) - 1;
    if (Number.isFinite(m) && m >= 0 && m <= 11) return m;
  }
  return null;
}

/** Trimestre 1–4 d'une note (d’après le mois de date / numéro). */
function overviewNoteTrimestre(note) {
  const m = overviewNoteMonth(note);
  if (m == null) return null;
  return Math.floor(m / 3) + 1;
}

/** Semestre 1–2 d'une note. */
function overviewNoteSemestre(note) {
  const m = overviewNoteMonth(note);
  if (m == null) return null;
  return m < 6 ? 1 : 2;
}
/** Normalise une valeur date en YYYY-MM-DD (stockage / API). */
function parseNhIsoDate(value, numeroFallback) {
  if (value == null || value === '') return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const yyyy = value.getFullYear();
    const mm = String(value.getMonth() + 1).padStart(2, '0');
    const dd = String(value.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
  const s = String(value).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const dmy = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  const localeWithYear = s.match(/^[A-Za-z]{3}\s+[A-Za-z]{3}\s+(\d{1,2})\s+(\d{4})/);
  if (localeWithYear) {
    const dd = String(localeWithYear[1]).padStart(2, '0');
    const yyyy = localeWithYear[2];
    const monthToken = s.split(/\s+/)[1];
    const monthIndex = [
      'jan', 'feb', 'mar', 'apr', 'may', 'jun',
      'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
    ].indexOf(String(monthToken || '').slice(0, 3).toLowerCase());
    if (monthIndex >= 0) {
      const mm = String(monthIndex + 1).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }
  }
  if (/^[A-Za-z]{3}\s+[A-Za-z]{3}\s+\d{1,2}$/.test(s)) {
    const fromNum = dateIsoFromNhNumero(numeroFallback);
    if (fromNum) return fromNum;
    return '';
  }
  const d = new Date(s);
  if (!Number.isNaN(d.getTime()) && d.getFullYear() >= 2000) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
  const fromNum = dateIsoFromNhNumero(numeroFallback);
  if (fromNum) return fromNum;
  return '';
}

/** Affichage Vue d’ensemble : DD/MM/YYYY */
function formatNhDisplayDate(value, numeroFallback) {
  let iso = parseNhIsoDate(value, numeroFallback);
  if (iso && iso.startsWith('2001-') && numeroFallback) {
    const repaired = dateIsoFromNhNumero(numeroFallback);
    if (repaired) iso = repaired;
  }
  if (!iso) return value == null ? '' : String(value).trim();
  const [yyyy, mm, dd] = iso.split('-');
  return `${dd}/${mm}/${yyyy}`;
}

function normalizeOverviewNoteDate(note) {
  if (!note || typeof note !== 'object') return note;
  const raw = note.date || note.createdAt || '';
  const normalized = parseNhIsoDate(raw, note.numero);
  return normalized ? { ...note, date: normalized } : note;
}

/** Attribue un ID unique (évite collision avec les IDs MySQL). */
function allocateUniqueOverviewId(usedIds) {
  let next = usedIds.size > 0 ? Math.max(...usedIds) + 1 : 1;
  while (usedIds.has(next)) next += 1;
  usedIds.add(next);
  return next;
}

/** Fusionne notes API (MySQL) et notes locales non encore synchronisées. */
function mergeNhOverviewNotes(apiNotes, localNotes, matricule) {
  const api = (apiNotes || []).map((n) =>
    normalizeOverviewNoteDate({
      ...n,
      matriculeComptable: n.matriculeComptable || matricule,
    })
  );
  const apiKeys = new Set(api.map((n) => noteNumeroKey(n.numero)));
  const usedIds = new Set(
    api.map((n) => Number(n.id)).filter((id) => Number.isFinite(id) && id > 0)
  );

  const filterByMatricule = matricule != null && String(matricule).trim() !== '';
  const localOnly = (localNotes || [])
    .filter(
      (n) =>
        n &&
        !n.autoPeriodKey &&
        (!filterByMatricule ||
          !n.matriculeComptable ||
          n.matriculeComptable === matricule) &&
        !apiKeys.has(noteNumeroKey(n.numero))
    )
    .map((n) => {
      const row = normalizeOverviewNoteDate({
        ...n,
        matriculeComptable: n.matriculeComptable || matricule,
      });
      const idNum = Number(row.id);
      if (!Number.isFinite(idNum) || idNum <= 0 || usedIds.has(idNum)) {
        row.id = allocateUniqueOverviewId(usedIds);
      } else {
        usedIds.add(idNum);
      }
      return row;
    });

  return sortOverviewNotesDesc([...api, ...localOnly]);
}

/** Résout l’id MySQL à partir du numéro NH (l’id local du navigateur peut être obsolète). */
async function fetchNhServerIdForNote(item, matricule) {
  const fallbackId = Number(item?.id);
  const mat = String(matricule || item?.matriculeComptable || '').trim();
  if (!item?.numero) {
    return Number.isFinite(fallbackId) && fallbackId > 0 ? fallbackId : null;
  }
  try {
    const res = await fetch(
      mat
        ? `/api/nh-notes?matricule=${encodeURIComponent(mat)}`
        : '/api/nh-notes'
    );
    const data = await res.json();
    if (data.success && Array.isArray(data.notes)) {
      const key = noteNumeroKey(item.numero);
      const match = data.notes.find((n) => noteNumeroKey(n.numero) === key);
      if (match && Number.isFinite(Number(match.id)) && Number(match.id) > 0) {
        return Number(match.id);
      }
    }
  } catch (error) {
    console.warn('NH : résolution id serveur', error);
  }
  return Number.isFinite(fallbackId) && fallbackId > 0 ? fallbackId : null;
}

/**
 * Champ Identification : évite que « 2 » matche par erreur dans l’année.
 * — ID et numéro : égalité exacte (normalisée).
 * — Si la saisie est uniquement des chiffres : partie série après le dernier « - » (ancien format), ou séquence du format NHMMYYYYseq.
 * — Sinon : sous-chaîne sur id ou numéro (ex. « NH042026 », « 268 » en texte mixte).
 */
function identificationMatchesNote(row, rawInput) {
  const trimmed = rawInput.trim();
  const q = trimmed.toLowerCase().replace(/\s/g, '');
  if (!q) return true;

  const idStr = String(row.id ?? '').trim();
  const idNorm = idStr.toLowerCase();
  const num = String(row.numero ?? '').toLowerCase().replace(/\s/g, '');
  const nk = noteNumeroKey(row.numero).toLowerCase().replace(/\s/g, '');

  if (idNorm === q) return true;
  if (num === q || nk === q) return true;

  const onlyDigits = /^\d+$/.test(trimmed);
  if (onlyDigits) {
    const qNum = parseInt(trimmed, 10);
    if (Number.isNaN(qNum)) return false;
    const dashParts = num.split('-');
    if (dashParts.length >= 2) {
      const lastSeg = dashParts[dashParts.length - 1].replace(/\D/g, '');
      if (lastSeg) {
        const seqNum = parseInt(lastSeg, 10);
        if (!Number.isNaN(seqNum) && seqNum === qNum) return true;
      }
    }
    const compactParsed = parseNhCompactNumero(num);
    if (compactParsed && compactParsed.seq === qNum) return true;
    // Ex. « 2026 » dans le numéro (sans faux positif sur un seul chiffre dans l’année)
    if (trimmed.length >= 4) {
      return idNorm.includes(q) || num.includes(q) || nk.includes(q);
    }
    return false;
  }

  return idNorm.includes(q) || num.includes(q) || nk.includes(q);
}

function parseTauxPercentFromNote(row) {
  const s = String(row.tauxTaxe ?? '0');
  const m = s.match(/(\d+(?:[.,]\d+)?)/);
  if (!m) return 0;
  return parseFloat(m[1].replace(',', '.')) || 0;
}

/** TTC à partir du montant HT et du taux (ex. « 19 % »). */
function computeTtcFromNoteRow(row) {
  const ht = parseFloat(String(row.montantHT ?? '0').replace(',', '.')) || 0;
  const tva = parseTauxPercentFromNote(row);
  return ht * (1 + tva / 100);
}

/** Même chaîne que la facture : ligne HT → TTC, timbre, R/S, net. */
function computeTotalsFromLineHTNH(lineHtRaw, tauxTaxe, fiscalParams) {
  const lineHt = parseFloat(String(lineHtRaw ?? '0').replace(',', '.')) || 0;
  const s = String(tauxTaxe ?? '0');
  const m = s.match(/(\d+(?:[.,]\d+)?)/);
  const tvaPct = m ? parseFloat(m[1].replace(',', '.')) || 0 : 0;
  const retenueSourceTaux = parseFloat(fiscalParams?.retenue ?? 3) || 0;
  const timbreFiscal = parseFloat(String(fiscalParams?.timbre ?? 1).replace(',', '.')) || 0;
  const montantTTC = lineHt * (1 + tvaPct / 100);
  const montantTVA = montantTTC - lineHt;
  const grandTotal = montantTTC + timbreFiscal;
  const retenueSource = montantTTC * (retenueSourceTaux / 100);
  const netAPayer = grandTotal - retenueSource;
  return {
    montantHT: lineHt,
    montantTTC,
    montantTVA,
    grandTotal,
    retenueSource,
    netAPayer,
    timbreFiscal,
    retenueSourceTaux,
    tvaPct,
  };
}

function getFiscalParamsForNoteRow(note, fiscalData) {
  if (note?.fiscalParams) return note.fiscalParams;
  const year = parseInt(String(note?.date || '').slice(0, 4), 10);
  if (Number.isFinite(year) && fiscalData[year]) return fiscalData[year];
  return fiscalData[2024] || { retenue: 3, timbre: 1 };
}

/** Recalcule net à payer (et aligne HT) à partir du montant HT — source de vérité vue d'ensemble. */
function syncNoteAmountsFromMontantHT(note, fiscalData) {
  const fp = getFiscalParamsForNoteRow(note, fiscalData);
  const totals = computeTotalsFromLineHTNH(note.montantHT, note.tauxTaxe, fp);
  return {
    ...note,
    fiscalParams: fp,
    montantHT: totals.montantHT.toFixed(3),
    netAPayer: totals.netAPayer.toFixed(3),
    amountSource: 'ht',
  };
}

/** Net à payer → PU HTVA / PU TTC de la ligne (inverse : R/S, timbre, TVA). */
function computeLinePuFromNetAPayer(net, tvaNum, fiscalParams) {
  const retenueSourceTaux = parseFloat(fiscalParams?.retenue ?? 3) || 0;
  const timbreFiscal = parseFloat(String(fiscalParams?.timbre ?? 1).replace(',', '.')) || 0;
  const rf = 1 - retenueSourceTaux / 100;
  const montantTTC = rf > 0 ? (net - timbreFiscal) / rf : net - timbreFiscal;
  const denom = 1 + (tvaNum / 100);
  const montantHT = denom !== 0 ? montantTTC / denom : 0;
  return {
    puHtv: montantHT === 0 ? '' : montantHT.toFixed(3),
    puTtc: montantTTC.toFixed(3),
  };
}

/**
 * Totaux d'une note multi-lignes (même formule que le backend).
 * TOTAL HT = Σ PU HTVA ; TVA = Σ (PU HTVA × taux/100) par ligne (0 % n'ajoute rien) ;
 * TTC = HT + TVA ; timbre unique ; R/S = TTC × retenue% ; net = TTC + timbre − R/S.
 */
function computeNoteTotalsFromLinesNH(lignes, fiscalParams) {
  const retenueSourceTaux = parseFloat(fiscalParams?.retenue ?? 3) || 0;
  const timbreFiscal = parseFloat(String(fiscalParams?.timbre ?? 1).replace(',', '.')) || 0;
  let montantHT = 0;
  let montantTVA = 0;
  const normalized = (Array.isArray(lignes) ? lignes : []).map((l, i) => {
    const ht = parseFloat(String(l.puHtv ?? l.pu_htva ?? l.montantHT ?? '0').replace(',', '.')) || 0;
    const taux = parseFloat(String(l.tauxTva ?? l.taux_tva ?? l.tauxTaxe ?? '0').replace(',', '.')) || 0;
    const tva = ht * (taux / 100);
    const ttc = ht + tva;
    montantHT += ht;
    montantTVA += tva;
    return {
      ordre: i + 1,
      designation: String(l.designation || '').trim(),
      tauxTva: taux,
      puHtv: ht.toFixed(3),
      puTtc: ttc.toFixed(3),
    };
  });
  const montantTTC = montantHT + montantTVA;
  const grandTotal = montantTTC + timbreFiscal;
  const retenueSource = montantTTC * (retenueSourceTaux / 100);
  const netAPayer = grandTotal - retenueSource;
  return {
    montantHT,
    montantTTC,
    montantTVA,
    grandTotal,
    retenueSource,
    netAPayer,
    timbreFiscal,
    retenueSourceTaux,
    lignes: normalized,
  };
}

export default function NHPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin, activeCabinet } = useAuth();
  const userIsAdmin = isAdmin();
  const nhInitialRef = useRef();
  if (nhInitialRef.current === undefined) {
    nhInitialRef.current = getInitialNhUiState(location.state);
  }
  const nhInitial = nhInitialRef.current;
  const mainContentRef = useRef(null);

  // --- ÉTATS (STATES) ---
  const [activeMenu, setActiveMenu] = useState(nhInitial.menu);
  const [expandMenu, setExpandMenu] = useState(true);
  const [overviewYearsExpanded, setOverviewYearsExpanded] = useState(
    () => nhInitial.menu === 'overview'
  );
  const [addYearsExpanded, setAddYearsExpanded] = useState(
    () => nhInitial.menu === 'add'
  );
  const [overviewSelectedYear, setOverviewSelectedYear] = useState(() =>
    nhDefaultSelectedYearForFiscal(ensureNhFiscalYearsThroughCalendar({ ...NH_INITIAL_FISCAL_DATA }))
  );
  const [selectedMatriculeComptable, setSelectedMatriculeComptable] = useState(nhInitial.matricule);
  const [overviewSelectedMonth, setOverviewSelectedMonth] = useState(''); // '' = tous les mois
  const [overviewSelectedTrimestre, setOverviewSelectedTrimestre] = useState(''); // '' | 1..4
  const [overviewSelectedSemestre, setOverviewSelectedSemestre] = useState(''); // '' | 1..2
  const [selectedYear, setSelectedYear] = useState(() =>
    nhDefaultSelectedYearForFiscal(ensureNhFiscalYearsThroughCalendar({ ...NH_INITIAL_FISCAL_DATA }))
  );
  const [fiscalData, setFiscalData] = useState(() =>
    ensureNhFiscalYearsThroughCalendar({ ...NH_INITIAL_FISCAL_DATA })
  );
  const [showFiscalForm, setShowFiscalForm] = useState(false);
  const [monthsMenuOpen, setMonthsMenuOpen] = useState(false);
  /** Menu latéral NH (mobile) — panneau coulissant */
  const [nhMobileNavOpen, setNhMobileNavOpen] = useState(false);
  const [newFiscalParams, setNewFiscalParams] = useState({
    annee: '',
    retenue: '',
    timbre: '',
    tva0: '',
    tva19: ''
  });
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [nhUiAlert, setNhUiAlert] = useState(null);
  const [selectedNoteForInvoice, setSelectedNoteForInvoice] = useState(null);
  /** Incrémenté à l’ouverture du formulaire « Dupliquer » depuis le tableau (prop Invoice). */
  const [invoiceDuplicateFormKey, setInvoiceDuplicateFormKey] = useState(0);
  const [nhClients, setNhClients] = useState([]);
  /** Tous les clients (pas seulement NH) — pour adresse / e-mail sur les factures. */
  const [clientRegistryForInvoice, setClientRegistryForInvoice] = useState([]);
  const [nhClientsLoading, setNhClientsLoading] = useState(false);
  const [nhClientsError, setNhClientsError] = useState('');
  const [honoPeriodeParClient, setHonoPeriodeParClient] = useState({});
  const [selectedClientHonoPeriode, setSelectedClientHonoPeriode] = useState('');
  const [selectedClientHonoMontant, setSelectedClientHonoMontant] = useState(0);

  // États pour les toggles de recherche
  const [searchIntervalle, setSearchIntervalle] = useState(false);
  const [searchOccurrences, setSearchOccurrences] = useState(true);
  const [searchDateNote, setSearchDateNote] = useState('');
  const [searchDateCreation, setSearchDateCreation] = useState('');
  const [searchIdentification, setSearchIdentification] = useState('');
  const [searchSociete, setSearchSociete] = useState('');
  const [searchDescriptions, setSearchDescriptions] = useState('');
  const [searchPrixTtc, setSearchPrixTtc] = useState('');
  const [searchIntervalStart, setSearchIntervalStart] = useState('');
  const [searchIntervalEnd, setSearchIntervalEnd] = useState('');
  /** `null` = recherche pas encore lancée ; tableau = derniers résultats */
  const [searchResults, setSearchResults] = useState(null);

  // États pour les options automatiques
  const [isAutoNumber, setIsAutoNumber] = useState(false);
  const [isToday, setIsToday] = useState(false);

  // État pour la visibilité des colonnes
  const [columnVisibility, setColumnVisibility] = useState({
    numero: true,
    designation: true,
    montantHT: true,
    tauxTaxe: true,
    date: true,
    societe: true,
    actions: true
  });
  const [selectedOverviewItem, setSelectedOverviewItem] = useState(null);
  /** Clé numéro NH en cours d'envoi e-mail (noteNumeroKey), ou null. */
  const [showEmailBatchModal, setShowEmailBatchModal] = useState(false);
  const [emailBatchSelected, setEmailBatchSelected] = useState({});
  const [emailBatchSending, setEmailBatchSending] = useState(false);
  const [emailBatchResults, setEmailBatchResults] = useState(null);

  // État pour le formulaire de modification
  const [editFormData, setEditFormData] = useState({
    numero: '',
    date: '',
    societe: ''
  });
  /** Lignes éditables de la note (Modifier la note) — supporte les notes multi-lignes. */
  const [editItems, setEditItems] = useState([
    { id: Date.now(), designation: '', tauxTva: 19, puHtv: '', puTtc: 0 }
  ]);

  // État pour le tableau de tâches (Description & Prix)
  const [items, setItems] = useState([
    { id: Date.now(), designation: '', tauxTva: 19, puHtv: '', puTtc: 0 }
  ]);
  /** Montant d'honoraires = net à payer ; la ligne PU est dérivée à l'inverse. */
  const [honoNetAPayer, setHonoNetAPayer] = useState('');
  const honoNetAPayerRef = useRef(honoNetAPayer);
  honoNetAPayerRef.current = honoNetAPayer;

  // États pour la pagination dans Vue d'Ensemble
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [overviewSearchQuery, setOverviewSearchQuery] = useState('');

  // Notes enregistrées (Ajouter une note → Vue d'ensemble), API + cache navigateur
  const [overviewData, setOverviewData] = useState(() => loadNhOverviewFromStorage());
  const [overviewLoading, setOverviewLoading] = useState(false);

  const allOverviewNotesSorted = useMemo(
    () => sortOverviewNotesDesc(overviewData),
    [overviewData]
  );

  const overviewForDisplay = useMemo(
    () => {
      if (!selectedMatriculeComptable) return allOverviewNotesSorted;
      return allOverviewNotesSorted.filter(
        (item) => !item.matriculeComptable || item.matriculeComptable === selectedMatriculeComptable
      );
    },
    [allOverviewNotesSorted, selectedMatriculeComptable]
  );
  const overviewForSelectedYear = useMemo(() => {
    let rows = overviewForDisplay;
    if (overviewSelectedYear) {
      rows = rows.filter((item) => overviewNoteYear(item) === overviewSelectedYear);
    }
    if (overviewSelectedMonth !== '') {
      rows = rows.filter((item) => overviewNoteMonth(item) === overviewSelectedMonth);
    }
    if (overviewSelectedTrimestre !== '') {
      rows = rows.filter(
        (item) => overviewNoteTrimestre(item) === overviewSelectedTrimestre
      );
    }
    if (overviewSelectedSemestre !== '') {
      rows = rows.filter(
        (item) => overviewNoteSemestre(item) === overviewSelectedSemestre
      );
    }
    return sortOverviewNotesDesc(rows);
  }, [
    overviewForDisplay,
    overviewSelectedYear,
    overviewSelectedMonth,
    overviewSelectedTrimestre,
    overviewSelectedSemestre,
  ]);
  useEffect(() => {
    setCurrentPage(1);
    setSelectedOverviewItem(null);
  }, [
    selectedMatriculeComptable,
    overviewSearchQuery,
    overviewSelectedYear,
    overviewSelectedMonth,
    overviewSelectedTrimestre,
    overviewSelectedSemestre,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  const lastNoteCreationDateIso = useMemo(
    () => nhLastNoteCreationDateIso(overviewData, selectedMatriculeComptable),
    [overviewData, selectedMatriculeComptable]
  );

  const showNhUiAlert = useCallback((opts) => {
    setNhUiAlert({
      tone: opts.tone || 'warning',
      title: opts.title || 'Attention',
      message: opts.message || '',
      detail: opts.detail || '',
    });
  }, []);

  const alertDateBeforeLastNote = useCallback((iso) => {
    showNhUiAlert({
      tone: 'warning',
      title: 'Date non autorisée',
      message: `La date ne peut pas être avant la dernière facture (${formatNhDisplayDate(iso)}).`,
      detail: 'Choisissez la même date ou une date postérieure.',
    });
  }, [showNhUiAlert]);

  /** Admin / SuperAdmin : peut tout modifier en édition (date, numéro, lignes…). */
  const canBypassNhDateControl = Boolean(userIsAdmin);

  const handleAddFormDateChange = useCallback(
    (value) => {
      const iso = String(value || '').slice(0, 10);
      if (!iso) {
        setIsToday(false);
        setFormData((prev) => ({ ...prev, dateNH: '' }));
        return;
      }
      // Création manuelle : date ≥ dernière facture (tous les utilisateurs)
      if (!nhIsDateAllowedForNewNote(iso, lastNoteCreationDateIso)) {
        alertDateBeforeLastNote(lastNoteCreationDateIso);
        return;
      }
      setIsToday(false);
      setFormData((prev) => ({ ...prev, dateNH: iso }));
    },
    [lastNoteCreationDateIso, alertDateBeforeLastNote]
  );

  const overviewRowsFiltered = useMemo(() => {
    const raw = overviewSearchQuery.trim().toLowerCase();
    if (!raw) return overviewForSelectedYear;
    const tokens = raw.split(/\s+/).filter(Boolean);
    const filtered = overviewForSelectedYear.filter((item) => {
      const hay = [
        item.id,
        item.numero,
        item.designation,
        item.montantHT,
        item.tauxTaxe,
        item.date,
        item.societe,
      ]
        .map((v) => (v == null ? '' : String(v).toLowerCase()))
        .join(' ');
      return tokens.every((token) => hay.includes(token));
    });
    return sortOverviewNotesDesc(filtered);
  }, [overviewForSelectedYear, overviewSearchQuery]);

  const handleOverviewYearSelect = useCallback((year) => {
    setOverviewSelectedYear(year);
    setActiveMenu('overview');
    setOverviewYearsExpanded(true);
    setAddYearsExpanded(false);
    setOverviewSearchQuery('');
    setCurrentPage(1);
    requestAnimationFrame(() => {
      mainContentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const handleAddYearSelect = useCallback((year) => {
    setSelectedYear(year);
    setActiveMenu('add');
    setAddYearsExpanded(true);
    setOverviewYearsExpanded(false);
    requestAnimationFrame(() => {
      mainContentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  // Nouvel An : créer l’année civile dans la liste et ouvrir dessus (vide tant qu’il n’y a pas de notes)
  useEffect(() => {
    const syncNhCalendarYear = () => {
      const cal = new Date().getFullYear();
      setFiscalData((prev) => {
        if (prev[cal]) return prev;
        const next = ensureNhFiscalYearsThroughCalendar(prev);
        setOverviewSelectedYear(cal);
        setSelectedYear(cal);
        setOverviewSelectedMonth('');
        setOverviewSelectedTrimestre('');
        setOverviewSelectedSemestre('');
        return next;
      });
    };
    syncNhCalendarYear();
    window.addEventListener('focus', syncNhCalendarYear);
    const timer = window.setInterval(syncNhCalendarYear, 60000);
    return () => {
      window.removeEventListener('focus', syncNhCalendarYear);
      window.clearInterval(timer);
    };
  }, []);

  // Définition des colonnes
  const columns = [
    { key: 'numero', label: 'Numéro' },
    { key: 'designation', label: 'Désignation' },
    { key: 'montantHT', label: 'Montant HT' },
    { key: 'tauxTaxe', label: 'Taux Taxe' },
    { key: 'date', label: 'Date' },
    { key: 'societe', label: 'Société/Client' },
    { key: 'actions', label: 'Action' }
  ];

  // ========== FONCTIONS D'EXPORTATION ==========

  // Fonction pour copier le tableau dans le presse-papier

  // Fonction pour exporter en Excel via HTML table (aucune dépendance externe)
  const handleExportExcel = () => {
    try {
      const visibleColumns = columns.filter(
        (col) =>
          columnVisibility[col.key] &&
          col.key !== 'actions' &&
          col.key !== 'modifier' &&
          col.key !== 'dupliquer' &&
          col.key !== 'supprimer'
      );

      const colWidths = { numero: 140, designation: 220, montantHT: 100, tauxTaxe: 80, date: 100, societe: 200 };

      let tableXml = '';
      visibleColumns.forEach((col) => {
        const w = colWidths[col.key] || 120;
        tableXml += `<Column ss:AutoFitWidth="0" ss:Width="${w}"/>`;
      });

      // Header row
      tableXml += '<Row ss:AutoFitHeight="0" ss:Height="28">';
      visibleColumns.forEach((col) => {
        tableXml += `<Cell ss:StyleID="header"><Data ss:Type="String">${col.label}</Data></Cell>`;
      });
      tableXml += '</Row>';

      // Data rows
      overviewRowsFiltered.forEach((item, idx) => {
        const style = idx % 2 === 0 ? 'dataEven' : 'dataOdd';
        tableXml += '<Row ss:AutoFitHeight="0" ss:Height="22">';
        visibleColumns.forEach((col) => {
          let raw = item[col.key] || '';
          if (col.key === 'date' && raw) {
            raw = formatNhDisplayDate(raw, item.numero);
          }
          const isNum = col.key === 'montantHT' && raw !== '' && !isNaN(Number(raw));
          const type = isNum ? 'Number' : 'String';
          const val = isNum ? Number(raw) : String(raw).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
          tableXml += `<Cell ss:StyleID="${style}"><Data ss:Type="${type}">${val}</Data></Cell>`;
        });
        tableXml += '</Row>';
      });

      const xml = `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
  xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
  xmlns:x="urn:schemas-microsoft-com:office:excel">
  <Styles>
    <Style ss:ID="Default"><Alignment ss:Vertical="Center" ss:Horizontal="Center" ss:WrapText="1"/></Style>
    <Style ss:ID="header">
      <Font ss:Bold="1" ss:Color="#FFFFFF" ss:Size="11" ss:FontName="Arial"/>
      <Interior ss:Color="#00BFFF" ss:Pattern="Solid"/>
      <Alignment ss:Vertical="Center" ss:Horizontal="Center" ss:WrapText="1"/>
      <Borders>
        <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="2"/>
        <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="2"/>
        <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="2"/>
        <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="2"/>
      </Borders>
    </Style>
    <Style ss:ID="dataEven">
      <Font ss:Size="10" ss:FontName="Arial"/>
      <Alignment ss:Vertical="Center" ss:Horizontal="Center" ss:WrapText="1"/>
      <Borders>
        <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/>
      </Borders>
    </Style>
    <Style ss:ID="dataOdd">
      <Font ss:Size="10" ss:FontName="Arial"/>
      <Interior ss:Color="#F5F5F5" ss:Pattern="Solid"/>
      <Alignment ss:Vertical="Center" ss:Horizontal="Center" ss:WrapText="1"/>
      <Borders>
        <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/>
      </Borders>
    </Style>
  </Styles>
  <Worksheet ss:Name="Notes d Honoraires">
    <Table>${tableXml}</Table>
    <WorksheetOptions xmlns="urn:schemas-microsoft-com:office:excel">
      <FreezePanes/><FrozenNoSplit/><SplitHorizontal>1</SplitHorizontal><TopRowBottomPane>1</TopRowBottomPane>
    </WorksheetOptions>
  </Worksheet>
</Workbook>`;

      const blob = new Blob([xml], { type: 'application/vnd.ms-excel' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `notes_honoraires_${new Date().toISOString().split('T')[0]}.xls`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      alert(`✅ Export Excel réussi !\n\n${overviewRowsFiltered.length} notes exportées.`);

    } catch (err) {
      console.error('❌ Erreur export Excel:', err);
      alert('❌ Erreur lors de l\'export Excel.\n\nMessage: ' + err.message);
    }
  };
  // 🎯 Fonction pour exporter en PDF avec html2canvas + jsPDF (même méthode que la facture)
  const handleExportPDF = async () => {
    try {
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');

      const visibleColumns = columns.filter(
        (col) => columnVisibility[col.key] && col.key !== 'actions'
      );

      const tableHtml = `
        <div style="padding:20px;font-family:Arial,sans-serif;background:#fff;width:1200px;">
          <h2 style="color:#007bff;margin-bottom:4px;">Notes d'Honoraires - Export Complet</h2>
          <p style="font-size:12px;color:#555;margin:0 0 4px;">
            Exporté le : ${new Date().toLocaleDateString('fr-FR')}
            &nbsp;|&nbsp; Total : ${overviewRowsFiltered.length} notes
          </p>
          <table style="width:100%;border-collapse:collapse;font-size:11px;">
            <thead>
              <tr>
                ${visibleColumns
          .map(
            (col) =>
              `<th style="background:#007bff;color:#fff;padding:6px 4px;border:1px solid #dee2e6;text-align:center;font-size:11px;">${col.label}</th>`
          )
          .join('')}
              </tr>
            </thead>
            <tbody>
              ${overviewRowsFiltered
          .map(
            (item, idx) =>
              `<tr style="background:${idx % 2 === 0 ? '#fff' : '#f5f5f5'};">
                      ${visibleColumns
                .map((col) => {
                  let raw = item[col.key] || '';
                  if (col.key === 'date' && raw) {
                    raw = formatNhDisplayDate(raw, item.numero);
                  }
                  return `<td style="padding:5px 4px;border:1px solid #dee2e6;text-align:center;font-size:10px;">${raw}</td>`;
                })
                .join('')}
                    </tr>`
          )
          .join('')}
            </tbody>
          </table>
        </div>
      `;

      const container = document.createElement('div');
      container.style.position = 'fixed';
      container.style.left = '-9999px';
      container.style.top = '0';
      container.innerHTML = tableHtml;
      document.body.appendChild(container);

      const canvas = await html2canvas(container.firstElementChild, {
        scale: 1.5,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
      });

      document.body.removeChild(container);

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
        compress: true,
      });

      const pageWidth = pdf.internal.pageSize.getWidth() - 20;
      const pageHeight = pdf.internal.pageSize.getHeight() - 20;
      const ratio = pageWidth / imgWidth;

      let yOffset = 0;
      let pageNum = 1;
      const sliceHeightPx = pageHeight / ratio;

      while (yOffset < imgHeight) {
        if (pageNum > 1) pdf.addPage();

        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = imgWidth;
        const currentSlice = Math.min(sliceHeightPx, imgHeight - yOffset);
        sliceCanvas.height = currentSlice;
        const ctx = sliceCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        ctx.drawImage(canvas, 0, -yOffset);

        const sliceData = sliceCanvas.toDataURL('image/jpeg', 0.78);
        pdf.addImage(sliceData, 'JPEG', 10, 10, pageWidth, currentSlice * ratio);

        pdf.setFontSize(8);
        pdf.setTextColor(150);
        pdf.text(
          `Page ${pageNum}`,
          10,
          pdf.internal.pageSize.getHeight() - 5
        );

        yOffset += sliceHeightPx;
        pageNum++;
      }

      const fileName = `notes_honoraires_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

      alert(`✅ PDF généré avec succès !\n\n${overviewRowsFiltered.length} notes exportées.`);

    } catch (err) {
      console.error('❌ Erreur export PDF:', err);
      alert('❌ Erreur lors de la génération du PDF.\n\nMessage: ' + err.message + '\n\nVérifiez que la bibliothèque jspdf est installée.');
    }
  };

  // Fonction pour imprimer
  const handlePrint = () => {
    window.print();
  };

  // ========== FIN FONCTIONS EXPORTATION ==========

  // Fonction pour afficher la facture
  const persistEnrichedNoteToOverview = (note, enriched) => {
    if (note?.id == null) return;
    const base = enrichInvoiceClientFromRegistry(note, clientRegistryForInvoice);
    const merged = enriched || base;
    if (
      !merged.adresse &&
      !merged.email &&
      !merged.clientId
    ) {
      return;
    }
    setOverviewData((prev) =>
      prev.map((row) =>
        Number(row.id) === Number(note.id)
          ? {
            ...row,
            clientId: merged.clientId ?? row.clientId,
            adresse: merged.adresse || row.adresse,
            email: merged.email || row.email,
            matriculeFiscale: merged.matriculeFiscale || row.matriculeFiscale,
          }
          : row
      )
    );
  };

  const handleViewInvoice = async (note) => {
    setInvoiceDuplicateFormKey(0);
    let enriched = enrichInvoiceClientFromRegistry(note, clientRegistryForInvoice);
    const needsApi =
      !normalizeInvoiceClientData(enriched).adresse &&
      (note?.clientId || note?.societe || note?.matriculeFiscale);
    if (needsApi) {
      enriched = await enrichInvoiceClientFromApi(note, clientRegistryForInvoice);
    }
    setSelectedNoteForInvoice(enriched);
    persistEnrichedNoteToOverview(note, enriched);
    setShowInvoiceModal(true);
  };

  /** Persistance après « Modifier la note » dans la facture (Invoice). */
  const handleInvoiceEditSave = async (updated) => {
    if (!userIsAdmin) return;
    const id = updated?.id != null ? updated.id : selectedNoteForInvoice?.id;
    if (id == null) return;

    const rawNumero = normalizeNumeroDisplay(updated?.numero);
    const numero = rawNumero
      ? (rawNumero.toUpperCase().startsWith('NH') ? rawNumero : `NH${rawNumero}`)
      : selectedNoteForInvoice?.numero;

    if (
      canBypassNhDateControl &&
      noteNumeroKey(numero) !== noteNumeroKey(selectedNoteForInvoice?.numero)
    ) {
      const conflict = overviewData.some(
        (r) => Number(r.id) !== Number(id) && noteNumeroKey(r.numero) === noteNumeroKey(numero)
      );
      if (conflict) {
        showNhUiAlert({
          tone: 'danger',
          title: 'Numéro déjà utilisé',
          message: `Le numéro « ${numero} » existe déjà.`,
          detail: 'Choisissez un autre numéro de note d’honoraires.',
        });
        return;
      }
    }

    const dateToSave = canBypassNhDateControl
      ? String(updated?.date || '').slice(0, 10) || selectedNoteForInvoice?.date
      : selectedNoteForInvoice?.date;

    let synced = {
      ...selectedNoteForInvoice,
      ...updated,
      id,
      numero: canBypassNhDateControl ? numero : selectedNoteForInvoice?.numero,
      date: dateToSave,
    };

    const noteId = Number(id);
    if (Number.isFinite(noteId) && noteId > 0) {
      try {
        const tauxMatch = String(synced.tauxTaxe || synced.tauxTva || '19').match(/(\d+(?:[.,]\d+)?)/);
        const tauxTva = tauxMatch ? parseFloat(tauxMatch[1].replace(',', '.')) : 19;
        const response = await fetch(`/api/nh-notes/${noteId}`, {
          method: 'PUT',
          headers: authJsonHeaders(),
          body: JSON.stringify({
            numero: synced.numero,
            date: synced.date,
            societe: synced.societe,
            adresse: synced.adresse,
            matriculeFiscale: synced.matriculeFiscale,
            matriculeComptable: synced.matriculeComptable,
            designation: synced.designation,
            montantHT: synced.montantHT,
            tauxTva,
            fiscalParams: synced.fiscalParams,
            natureContrat: synced.natureContrat || 'CONVENTION',
            ...(Array.isArray(synced.lignes) && synced.lignes.length > 1
              ? { lignes: synced.lignes }
              : {}),
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (response.ok && data.success && data.note) {
          synced = {
            ...synced,
            ...data.note,
            numero: data.note.numero || synced.numero,
            matriculeComptable: data.note.matriculeComptable || synced.matriculeComptable,
          };
        } else if (!response.ok) {
          showNhUiAlert({
            tone: data.code === 'DUPLICATE_NUMERO' ? 'danger' : 'warning',
            title: data.code === 'DUPLICATE_NUMERO' ? 'Numéro déjà utilisé' : 'Enregistrement impossible',
            message: data.error || 'La note n’a pas pu être enregistrée.',
            detail:
              data.code === 'DUPLICATE_NUMERO'
                ? 'Modifiez le numéro puis réessayez.'
                : '',
          });
          return;
        }
      } catch (error) {
        console.warn('NH : mise à jour facture', error);
        showNhUiAlert({
          tone: 'danger',
          title: 'Erreur réseau',
          message: 'Impossible d’enregistrer la note. Réessayez.',
        });
        return;
      }
    }

    setOverviewData((prev) =>
      prev.map((row) => (Number(row.id) !== Number(id) ? row : { ...row, ...synced, id: row.id }))
    );
    setSelectedNoteForInvoice((prev) =>
      !prev || Number(prev.id) !== Number(id) ? prev : { ...prev, ...synced, id }
    );
  };

  /** Ajoute la note dupliquée (Invoice) en tête de liste et affiche la nouvelle note. */
  const handleInvoiceDuplicate = async (dup) => {
    if (!dup || typeof dup !== 'object') return;
    const noteDate = dup.date
      ? String(dup.date).trim().slice(0, 10)
      : new Date().toISOString().slice(0, 10);
    const matricule = dup.matriculeComptable || selectedMatriculeComptable || null;
    if (!matricule) {
      window.alert('Sélectionnez un matricule comptable avant de dupliquer une note.');
      return;
    }

    const usedIds = new Set(
      overviewData.map((r) => Number(r.id)).filter((id) => Number.isFinite(id) && id > 0)
    );
    const nextId = allocateUniqueOverviewId(usedIds);
    const { id: _oldId, ...rest } = dup;
    const serverMax = await fetchNhDbMaxSeqForDate(noteDate, matricule, activeCabinet?.id);
    const numero = nextNhAutoNumeroAfterMerge(noteDate, overviewData, serverMax, matricule);
    const createdAt = new Date().toISOString().slice(0, 10);
    let createdNew = {
      ...rest,
      id: nextId,
      numero,
      date: noteDate,
      createdAt,
      matriculeComptable: matricule,
    };

    const tauxMatch = String(rest.tauxTaxe || '19').match(/(\d+(?:[.,]\d+)?)/);
    const tauxTva = tauxMatch ? parseFloat(tauxMatch[1].replace(',', '.')) : 19;
    const netRaw = rest.netAPayer ?? rest.montantHT;
    const netAPayer = parseFloat(String(netRaw).replace(',', '.'));
    if (Number.isFinite(netAPayer) && netAPayer > 0) {
      try {
        const response = await fetch('/api/nh-notes', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            matriculeComptable: matricule,
            numero,
            designation: rest.designation || '',
            montantHT: rest.montantHT,
            netAPayer: netAPayer.toFixed(3),
            tauxTva,
            date: noteDate,
            societe: rest.societe || '',
            adresse: rest.adresse || '',
            matriculeFiscale: rest.matriculeFiscale || '',
            email: rest.email || '',
            clientId: rest.clientId || null,
            fiscalParams: rest.fiscalParams,
            natureContrat: rest.natureContrat || 'CONVENTION',
          }),
        });

        const data = await response.json();
        if (response.ok && data.success && data.note) {
          createdNew = {
            ...data.note,
            matriculeComptable: data.note.matriculeComptable || matricule,
          };
        } else if (!response.ok && data.error) {
          if (response.status === 409) {
            window.alert(data.error);
            return;
          }
          console.warn('NH : duplication serveur', data.error);
        }
      } catch (error) {
        console.warn('NH : duplication serveur indisponible, note locale uniquement', error);
      }
    }


    setOverviewData((prev) => {
      const withoutDup = prev.filter(
        (r) => noteNumeroKey(r.numero) !== noteNumeroKey(createdNew.numero)
      );
      return sortOverviewNotesDesc([createdNew, ...withoutDup]);
    });
    setSelectedNoteForInvoice(createdNew);
    const noteYear = overviewNoteYear(createdNew);
    handleOverviewYearSelect(noteYear || overviewSelectedYear);

    autoExportNotePdf(createdNew, clientRegistryForInvoice);
  };

  const handleDuplicateNoteFromRow = (item, e) => {
    if (e) e.stopPropagation();
    setSelectedNoteForInvoice(item);
    setInvoiceDuplicateFormKey((k) => k + 1);
    setShowInvoiceModal(true);
  };

  const handleModifyNote = (item) => {
    if (!userIsAdmin) return;
    setEditingItem(item);

    const lignesSource =
      Array.isArray(item.lignes) && item.lignes.length > 0
        ? item.lignes
        : [
          {
            designation: item.designation || '',
            tauxTva: parseTauxPercentFromNote(item),
            puHtv: item.montantHT || '',
          },
        ];

    setEditItems(
      lignesSource.map((l, idx) => {
        const ht =
          parseFloat(String(l.puHtv ?? l.pu_htva ?? l.montantHT ?? '0').replace(',', '.')) || 0;
        const taux =
          parseFloat(String(l.tauxTva ?? l.taux_tva ?? l.tauxTaxe ?? '0').replace(',', '.')) || 0;
        return {
          id: Date.now() + idx,
          designation: l.designation || '',
          tauxTva: taux,
          puHtv: ht === 0 ? '' : ht.toFixed(3),
          puTtc: (ht * (1 + taux / 100)).toFixed(3),
        };
      })
    );

    setEditFormData({
      numero: item.numero,
      date: item.date,
      societe: item.societe
    });
    setShowEditModal(true);
  };

  /** Gère les changements dans une ligne du tableau « Modifier la note ». */
  const handleEditItemChange = (id, field, value) => {
    setEditItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const updated = { ...item, [field]: value };
        const tva = parseFloat(String(field === 'tauxTva' ? value : item.tauxTva).replace(',', '.')) || 0;
        if (field === 'puHtv') {
          const ht = parseFloat(String(value).replace(',', '.')) || 0;
          updated.puTtc = (ht * (1 + tva / 100)).toFixed(3);
        } else if (field === 'tauxTva') {
          const ht = parseFloat(String(item.puHtv).replace(',', '.')) || 0;
          updated.puTtc = (ht * (1 + tva / 100)).toFixed(3);
        } else if (field === 'puTtc') {
          const ttc = parseFloat(String(value).replace(',', '.')) || 0;
          const denom = 1 + tva / 100;
          const ht = denom > 0 ? ttc / denom : 0;
          updated.puHtv = ht === 0 ? '' : String(ht);
          updated.puTtc = ttc === 0 ? '0' : ttc.toFixed(3);
        }
        return updated;
      })
    );
  };

  const addEditItem = () => {
    setEditItems((prev) => [
      ...prev,
      { id: Date.now(), designation: '', tauxTva: 19, puHtv: '', puTtc: 0 },
    ]);
  };

  const removeEditItem = (id) => {
    setEditItems((prev) => (prev.length > 1 ? prev.filter((it) => it.id !== id) : prev));
  };

  // Fonction pour sauvegarder les modifications (toutes les lignes + numéro modifiable)
  const handleSaveEdit = async () => {
    if (!userIsAdmin) return;
    const fp = getFiscalParamsForNoteRow(editingItem, fiscalData);

    const parsedLines = editItems
      .map((it) => ({
        designation: it.designation || '',
        tauxTva: parseFloat(String(it.tauxTva ?? '0').replace(',', '.')) || 0,
        puHtv: parseFloat(String(it.puHtv ?? '0').replace(',', '.')) || 0,
      }))
      .filter((l) => l.puHtv > 0);

    if (parsedLines.length === 0) {
      window.alert('Veuillez saisir au moins une ligne avec un montant HT.');
      return;
    }

    const isMulti = parsedLines.length > 1;
    const totals = computeNoteTotalsFromLinesNH(parsedLines, fp);

    const rawNumero = normalizeNumeroDisplay(editFormData.numero);
    const numero = rawNumero
      ? (rawNumero.toUpperCase().startsWith('NH') ? rawNumero : `NH${rawNumero}`)
      : editingItem.numero;

    if (noteNumeroKey(numero) !== noteNumeroKey(editingItem.numero)) {
      if (!canBypassNhDateControl) {
        showNhUiAlert({
          tone: 'warning',
          title: 'Modification réservée',
          message: 'Seul un administrateur peut modifier le numéro de la note.',
        });
        return;
      }
      const conflict = overviewData.some(
        (r) => r.id !== editingItem.id && noteNumeroKey(r.numero) === noteNumeroKey(numero)
      );
      if (conflict) {
        showNhUiAlert({
          tone: 'danger',
          title: 'Numéro déjà utilisé',
          message: `Le numéro « ${numero} » existe déjà.`,
          detail: 'Choisissez un autre numéro de note d’honoraires.',
        });
        return;
      }
    }

    // Caissier : date non modifiable. Admin : date libre.
    const dateToSave = canBypassNhDateControl
      ? String(editFormData.date || '').slice(0, 10) || editingItem.date
      : editingItem.date;
    if (
      !canBypassNhDateControl &&
      String(editFormData.date || '').slice(0, 10) !== String(editingItem.date || '').slice(0, 10)
    ) {
      showNhUiAlert({
        tone: 'warning',
        title: 'Modification réservée',
        message: 'Seul un administrateur peut modifier la date d’une note d’honoraires.',
      });
      return;
    }

    let synced = {
      ...editingItem,
      numero,
      date: dateToSave,
      societe: editFormData.societe,
      designation: totals.lignes[0]?.designation || '',
      montantHT: totals.montantHT.toFixed(3),
      netAPayer: totals.netAPayer.toFixed(3),
      tauxTaxe: `${totals.lignes[0]?.tauxTva ?? 0}%`,
      fiscalParams: fp,
      lignes: isMulti ? totals.lignes : undefined,
    };



    const noteId = Number(synced.id);
    if (Number.isFinite(noteId) && noteId > 0) {
      try {
        const tauxMatch = String(synced.tauxTaxe || '19').match(/(\d+(?:[.,]\d+)?)/);
        const tauxTva = tauxMatch ? parseFloat(tauxMatch[1].replace(',', '.')) : 19;
        const response = await fetch(`/api/nh-notes/${noteId}`, {
          method: 'PUT',
          headers: authJsonHeaders(),
          body: JSON.stringify({
            numero,
            date: synced.date,
            societe: synced.societe,
            adresse: synced.adresse,
            matriculeFiscale: synced.matriculeFiscale,
            matriculeComptable: synced.matriculeComptable,
            designation: synced.designation,
            montantHT: synced.montantHT,
            tauxTva,
            fiscalParams: synced.fiscalParams,
            natureContrat: synced.natureContrat || 'CONVENTION',
            ...(isMulti ? { lignes: totals.lignes } : {}),
          }),
        });
        const data = await response.json();
        if (response.ok && data.success && data.note) {
          synced = {
            ...synced,
            ...data.note,
            numero: data.note.numero || numero,
            matriculeComptable: data.note.matriculeComptable || synced.matriculeComptable,
          };
          // Si le serveur ne renvoie pas encore les lignes multiples (route PUT
          // pas encore adaptée), on garde le calcul multi-lignes calculé côté client.
          if (isMulti && !Array.isArray(data.note.lignes)) {
            synced.lignes = totals.lignes;
            synced.montantHT = totals.montantHT.toFixed(3);
            synced.netAPayer = totals.netAPayer.toFixed(3);
            synced.designation = totals.lignes[0]?.designation || '';
            synced.numero = numero;
          }
        } else if (!response.ok && data.error) {
          showNhUiAlert({
            tone: data.code === 'DUPLICATE_NUMERO' ? 'danger' : 'warning',
            title: data.code === 'DUPLICATE_NUMERO' ? 'Numéro déjà utilisé' : 'Enregistrement impossible',
            message: data.error,
            detail:
              data.code === 'DUPLICATE_NUMERO'
                ? 'Modifiez le numéro puis réessayez.'
                : '',
          });
          return;
        }
      } catch (error) {
        console.warn('NH : mise à jour serveur', error);
      }
    }

    setOverviewData((prev) =>
      sortOverviewNotesDesc(
        prev.map((item) => (item.id === editingItem.id ? synced : item))
      )
    );
    if (selectedNoteForInvoice && Number(selectedNoteForInvoice.id) === Number(editingItem.id)) {
      setSelectedNoteForInvoice(synced);
    }
    setShowEditModal(false);
    setEditingItem(null);
    alert('Note modifiée avec succès!');
  };

  const handleDeleteNote = async (item) => {
    if (!userIsAdmin) return;
    const label = item.numero || `ID ${item.id}`;
    if (!window.confirm(`Supprimer la note ${label} ?`)) return;

    const matricule = item.matriculeComptable || selectedMatriculeComptable;
    const noteId = await fetchNhServerIdForNote(item, matricule);

    if (noteId != null) {
      try {
        const response = await fetch(`/api/nh-notes/${noteId}`, { method: 'DELETE' });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          if (response.status === 404) {
            const removeLocal = window.confirm(
              `La note ${label} est introuvable dans la base de données (données locales obsolètes). La retirer de la liste ?`
            );
            if (!removeLocal) return;
          } else {
            window.alert(data.error || 'Impossible de supprimer la note sur le serveur.');
            return;
          }
        }
      } catch (error) {
        console.warn('NH : suppression serveur indisponible', error);
        window.alert('Erreur réseau : la note n\'a pas été supprimée de la base.');
        return;
      }
    } else {
      const removeLocal = window.confirm(
        `La note ${label} n'est pas liée au serveur. La retirer de la liste locale ?`
      );
      if (!removeLocal) return;
    }

    const itemId = Number(item.id);
    setOverviewData((prev) =>
      prev.filter(
        (row) =>
          Number(row.id) !== itemId &&
          noteNumeroKey(row.numero) !== noteNumeroKey(item.numero)
      )
    );
    if (editingItem && Number(editingItem.id) === itemId) {
      setShowEditModal(false);
      setEditingItem(null);
    }
  };

  const getEmailBatchKey = (item) =>
    noteNumeroKey(item?.numero) || `id-${item?.id ?? ''}`;

  const openEmailBatchModal = (preselectItems = []) => {
    const initial = {};
    overviewRowsFiltered.forEach((row) => {
      initial[getEmailBatchKey(row)] = false;
    });
    (Array.isArray(preselectItems) ? preselectItems : [preselectItems])
      .filter(Boolean)
      .forEach((row) => {
        initial[getEmailBatchKey(row)] = true;
      });
    setEmailBatchSelected(initial);
    setEmailBatchResults(null);
    setShowEmailBatchModal(true);
  };

  const closeEmailBatchModal = () => {
    if (emailBatchSending) return;
    setShowEmailBatchModal(false);
    setEmailBatchResults(null);
  };

  const toggleEmailBatchItem = (key) => {
    setEmailBatchSelected((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleEmailBatchAll = (checked) => {
    const next = {};
    overviewRowsFiltered.forEach((row) => {
      next[getEmailBatchKey(row)] = checked;
    });
    setEmailBatchSelected(next);
  };

  const emailBatchSelectedCount = useMemo(
    () =>
      overviewRowsFiltered.filter((row) => emailBatchSelected[getEmailBatchKey(row)])
        .length,
    [overviewRowsFiltered, emailBatchSelected]
  );

  const emailBatchAllSelected =
    overviewRowsFiltered.length > 0 &&
    emailBatchSelectedCount === overviewRowsFiltered.length;

  const sendEmailForNoteItem = async (item) => {
    const label = item.numero || `ID ${item.id}`;
    const matricule = item.matriculeComptable || selectedMatriculeComptable;
    const noteId = await fetchNhServerIdForNote(item, matricule);
    if (noteId == null) {
      return {
        item,
        label,
        status: 'error',
        message: 'Note non enregistrée en base',
      };
    }
    try {
      const response = await fetch(`/api/nh-notes/${noteId}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ forceResend: true }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return { item, label, status: 'error', message: data.error || 'Échec envoi' };
      }
      if (data.status === 'sent') {
        return {
          item,
          label,
          status: 'sent',
          message: data.email || item.email || '',
        };
      }
      if (data.status === 'skipped') {
        return {
          item,
          label,
          status: 'skipped',
          message: data.detail || data.reason || 'PDF ou e-mail manquant',
        };
      }
      return {
        item,
        label,
        status: 'error',
        message: data.error || data.status || 'Échec',
      };
    } catch (error) {
      return { item, label, status: 'error', message: error.message };
    }
  };

  const handleConfirmEmailBatch = async () => {
    const toSend = overviewRowsFiltered.filter(
      (row) => emailBatchSelected[getEmailBatchKey(row)]
    );
    if (toSend.length === 0) return;
    setEmailBatchSending(true);
    setEmailBatchResults(null);
    const results = [];
    for (const item of toSend) {
      // eslint-disable-next-line no-await-in-loop
      const result = await sendEmailForNoteItem(item);
      results.push(result);
    }
    setEmailBatchResults(results);
    setEmailBatchSending(false);
  };

  // Fonction pour annuler la modification
  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingItem(null);
    setEditFormData({ numero: '', date: '', societe: '' });
    setEditItems([{ id: Date.now(), designation: '', tauxTva: 19, puHtv: '', puTtc: 0 }]);
  };

  // Fonction pour réinitialiser la recherche
  const handleResetSearch = () => {
    setSearchIntervalle(false);
    setSearchOccurrences(true);
    setSearchDateNote('');
    setSearchDateCreation('');
    setSearchIdentification('');
    setSearchSociete('');
    setSearchDescriptions('');
    setSearchPrixTtc('');
    setSearchIntervalStart('');
    setSearchIntervalEnd('');
    setSearchResults(null);
  };

  // Recherche sur les notes déjà enregistrées (localStorage / même matricule que la vue d’ensemble)
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    let rows = [...allOverviewNotesSorted];

    const idRaw = searchIdentification.trim();
    if (idRaw) {
      rows = rows.filter((r) => identificationMatchesNote(r, idRaw));
    }

    if (searchDateNote) {
      rows = rows.filter((r) => String(r.date || '') === searchDateNote);
    }

    if (searchDateCreation) {
      rows = rows.filter((r) => String(r.createdAt || r.date || '') === searchDateCreation);
    }

    if (searchSociete.trim()) {
      const sq = searchSociete.trim().toLowerCase();
      rows = rows.filter(
        (r) =>
          String(r.societe || '').toLowerCase().includes(sq) ||
          String(r.matriculeFiscale || '').toLowerCase().includes(sq)
      );
    }

    const descRaw = searchDescriptions.trim();
    if (descRaw) {
      const parts = descRaw.split(';').map((x) => x.trim()).filter(Boolean);
      if (parts.length) {
        rows = rows.filter((r) => {
          const d = String(r.designation || '').toLowerCase();
          return parts.some((p) => d.includes(p.toLowerCase()));
        });
      }
    }

    if (searchIntervalle && (searchIntervalStart || searchIntervalEnd)) {
      rows = rows.filter((r) => {
        const d = String(r.date || '');
        if (!d) return false;
        if (searchIntervalStart && d < searchIntervalStart) return false;
        if (searchIntervalEnd && d > searchIntervalEnd) return false;
        return true;
      });
    }

    const ttcRaw = searchPrixTtc.trim().replace(/\s/g, '').replace(',', '.');
    if (ttcRaw !== '') {
      const target = parseFloat(ttcRaw);
      if (!Number.isNaN(target)) {
        rows = rows.filter((r) => {
          const ttc = computeTtcFromNoteRow(r);
          return Math.abs(ttc - target) <= 0.002;
        });
      }
    }

    rows.sort((a, b) => {
      const da = String(a.date || '');
      const db = String(b.date || '');
      if (da !== db) return db.localeCompare(da);
      return (Number(b.id) || 0) - (Number(a.id) || 0);
    });

    const cap = 500;

    if (!searchOccurrences) {
      rows = rows.slice(0, 1);
    } else {
      rows = rows.slice(0, cap);
    }

    setSearchResults(rows);
  };

  const normalizeNumeroDisplay = (v) => {
    if (v == null) return '';
    const s = String(v).trim();
    if (s === '' || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined') return '';
    return s;
  };

  const buildNoteNumber = () => {
    const rawNumber = normalizeNumeroDisplay(formData.numeroNH);
    if (rawNumber) {
      const withPrefix = rawNumber.toUpperCase().startsWith('NH') ? rawNumber : `NH${rawNumber}`;
      return withPrefix;
    }
    const d = formData.dateNH || new Date().toISOString().slice(0, 10);
    return nextNhAutoNumeroCompact(d, overviewData, selectedMatriculeComptable);
  };

  const handleAddNote = async () => {
    const hasDate = isToday || String(formData.dateNH || '').trim() !== '';
    if (isAutoNumber && !hasDate) {
      window.alert('Veuillez renseigner la date (ou cocher « Aujourd\'hui ») avant d\'enregistrer la note avec un numéro automatique.');
      return;
    }
    const fp = fiscalData[selectedYear] || fiscalData[2024];

    // Lignes valides saisies dans le tableau « Tâches de la NH ».
    const parsedLines = items
      .map((it) => ({
        designation: it.designation || '',
        tauxTva: parseFloat(String(it.tauxTva ?? '0').replace(',', '.')) || 0,
        puHtv: parseFloat(String(it.puHtv ?? '0').replace(',', '.')) || 0,
      }))
      .filter((l) => l.puHtv > 0);
    const isMulti = parsedLines.length > 1;

    const firstItem = items[0] || {};
    const tauxTva = parseFloat(String(firstItem.tauxTva || '0').replace(',', '.')) || 0;
    const tauxTaxeStr = `${tauxTva}%`;

    let netAPayer;
    let lineHtNum;
    let payloadLignes = null;

    if (isMulti) {
      // Plusieurs lignes : TOTAL HT = somme des PU HTVA, TVA par ligne, net global.
      const multiTotals = computeNoteTotalsFromLinesNH(parsedLines, fp);
      netAPayer = multiTotals.netAPayer;
      lineHtNum = multiTotals.montantHT;
      payloadLignes = multiTotals.lignes;
    } else {
      const netParsed = parseFloat(String(honoNetAPayer).replace(/\s/g, '').replace(',', '.'));
      if (Number.isFinite(netParsed) && netParsed > 0) {
        netAPayer = netParsed;
        const inv = computeLinePuFromNetAPayer(netAPayer, tauxTva, fp);
        lineHtNum = parseFloat(String(inv.puHtv || '0').replace(',', '.')) || 0;
      } else {
        const htNum = parseFloat(String(firstItem.puHtv || '0').replace(',', '.')) || 0;
        if (htNum <= 0) {
          window.alert('Veuillez saisir le montant d\'hono (net à payer) ou un PU HTVA sur la première ligne.');
          return;
        }
        const fwd = computeTotalsFromLineHTNH(firstItem.puHtv, tauxTaxeStr, fp);
        netAPayer = fwd.netAPayer;
        lineHtNum = htNum;
      }
    }
    if (!Number.isFinite(netAPayer) || netAPayer <= 0) {
      window.alert('Montant d\'hono (net à payer) invalide.');
      return;
    }
    const noteDate = formData.dateNH || nhTodayIsoLocal();
    if (!nhIsDateAllowedForNewNote(noteDate, lastNoteCreationDateIso)) {
      alertDateBeforeLastNote(lastNoteCreationDateIso);
      return;
    }

    let serverMax = 0;
    if (isAutoNumber) {
      serverMax = await fetchNhDbMaxSeqForDate(noteDate, selectedMatriculeComptable, activeCabinet?.id);
    }

    let numero = '';
    if (isAutoNumber) {
      numero = nextNhAutoNumeroAfterMerge(noteDate, overviewData, serverMax, selectedMatriculeComptable);
    } else {
      const raw = normalizeNumeroDisplay(formData.numeroNH);
      numero = raw ? (raw.toUpperCase().startsWith('NH') ? raw : `NH${raw}`) : buildNoteNumber();
    }

    const usedKeys = new Set(overviewData.map((r) => noteNumeroKey(r.numero)));
    if (usedKeys.has(noteNumeroKey(numero))) {
      if (!isAutoNumber) {
        window.alert('Ce numéro de note existe déjà. Choisissez un autre numéro.');
        return;
      }
      let guard = 0;
      while (usedKeys.has(noteNumeroKey(numero)) && guard < 100) {
        const p = parseNhCompactNumero(numero);
        if (p) {
          numero = `NH${p.mm}${p.yyyy}${String(p.seq + 1).padStart(3, '0')}`;
        } else {
          const raw = String(numero).replace(/^NH/gi, '').trim();
          const m = raw.match(/^(\d{4})-(\d+)$/);
          if (m) {
            const nextSeq = parseInt(m[2], 10) + 1;
            numero = `NH${m[1]}-${String(nextSeq).padStart(4, '0')}`;
          } else {
            break;
          }
        }
        guard += 1;
      }
      if (guard >= 100) {
        window.alert('Impossible d\'attribuer un numéro de note unique. Vérifiez les numéros existants.');
        return;
      }
    }

    const payload = {
      matriculeComptable: selectedMatriculeComptable,
      numero,
      designation: (isMulti ? payloadLignes[0]?.designation : firstItem.designation) || '',
      montantHT: lineHtNum.toFixed(3),
      netAPayer: netAPayer.toFixed(3),
      tauxTva: tauxTva,
      date: noteDate,
      societe: formData.societe || '',
      adresse: formData.adresse || '',
      matriculeFiscale: formData.codeTva || '',
      email: formData.email || '',
      clientId: selectedClientId || null,
      fiscalParams: currentFiscalParams,
      natureContrat,
      // Envoyé uniquement en multi-lignes ; le backend bascule sur le calcul par lignes.
      ...(isMulti ? { lignes: payloadLignes } : {}),
    };

    const usedIds = new Set(
      overviewData.map((item) => Number(item.id)).filter((id) => Number.isFinite(id) && id > 0)
    );
    let note = {
      id: allocateUniqueOverviewId(usedIds),
      ...payload,
      tauxTaxe: `${tauxTva}%`,
      createdAt: new Date().toISOString().slice(0, 10),
      source: 'manual',
    };

    try {
      const response = await fetch('/api/nh-notes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (response.ok && data.success && data.note) {
        // Garder le matricule du cabinet actif : la réponse API pouvait retomber sur CGM1
        // et afficher le mauvais en-tête juste après création (OK ensuite via la table).
        note = {
          ...data.note,
          matriculeComptable:
            data.note.matriculeComptable ||
            selectedMatriculeComptable ||
            payload.matriculeComptable,
        };
      } else if (!response.ok && data.error) {
        console.warn('NH : enregistrement serveur', data.error);
        if (response.status === 409) {
          window.alert(data.error);
          return;
        }
      }
    } catch (error) {
      console.warn('NH : enregistrement serveur indisponible, note locale uniquement', error);
    }

    setOverviewData((prev) => {
      const withoutDup = prev.filter((r) => noteNumeroKey(r.numero) !== noteNumeroKey(note.numero));
      return sortOverviewNotesDesc([note, ...withoutDup]);
    });
    setSelectedNoteForInvoice(note);
    setInvoiceDuplicateFormKey(0);
    setShowInvoiceModal(true);
    const noteYear = overviewNoteYear(note);
    handleOverviewYearSelect(noteYear || overviewSelectedYear);

    autoExportNotePdf(note, clientRegistryForInvoice);
  };

  // Calcul de la pagination
  const totalItems = overviewRowsFiltered.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = overviewRowsFiltered.slice(indexOfFirstItem, indexOfLastItem);

  // Générer les numéros de pages à afficher (max 6 pages visibles)
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisible = 6;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 5; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    return pageNumbers;
  };

  // Fonctions de pagination
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const paginationFrom = totalItems === 0 ? 0 : indexOfFirstItem + 1;
  const paginationTo = totalItems === 0 ? 0 : Math.min(indexOfLastItem, totalItems);

  // Données du formulaire d'ajout (date = année / mois « présents » par défaut)
  const [formData, setFormData] = useState(() => ({
    numeroNH: '',
    dateNH: nhDefaultDateNHForAddForm(ensureNhFiscalYearsThroughCalendar({ ...NH_INITIAL_FISCAL_DATA })),
    societe: '',
    adresse: '',
    codeTva: '',
    email: ''
  }));
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [natureContrat, setNatureContrat] = useState('CONVENTION');

  useEffect(() => {
    const fetchNhClients = async () => {
      setNhClientsLoading(true);
      setNhClientsError('');
      try {
        const [response, honoResponse] = await Promise.all([
          fetch('/api/clients'),
          fetch('/api/client-hono-periode')
        ]);
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Impossible de charger les clients');
        }
        const allClients = data.clients || [];
        setClientRegistryForInvoice(buildClientRegistryFromApiClients(allClients));
        setNhClients(allClients);

        if (honoResponse.ok) {
          const honoData = await honoResponse.json();
          const periodeMap = {};
          (honoData.rows || []).forEach(row => {
            if (!periodeMap[row.client_id]) {
              periodeMap[row.client_id] = {};
            }
            periodeMap[row.client_id][row.periode_type] = row;
          });
          setHonoPeriodeParClient(periodeMap);
        }
      } catch (error) {
        setNhClientsError(error.message || 'Erreur lors du chargement des clients');
        setNhClients([]);
        setClientRegistryForInvoice([]);
        setHonoPeriodeParClient({});
      } finally {
        setNhClientsLoading(false);
      }
    };

    fetchNhClients();
  }, []);

  /** Notes anciennes : compléter adresse / e-mail dès que la liste clients est chargée. */
  useEffect(() => {
    if (!clientRegistryForInvoice.length) return;
    setOverviewData((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        const enriched = enrichInvoiceClientFromRegistry(row, clientRegistryForInvoice);
        if (
          (enriched.adresse || '') === (row.adresse || '') &&
          (enriched.email || '') === (row.email || '') &&
          (enriched.clientId ?? null) === (row.clientId ?? null) &&
          (enriched.matriculeFiscale || '') === (row.matriculeFiscale || '')
        ) {
          return row;
        }
        changed = true;
        return {
          ...row,
          clientId: enriched.clientId ?? row.clientId,
          adresse: enriched.adresse || row.adresse,
          email: enriched.email || row.email,
          matriculeFiscale: enriched.matriculeFiscale || row.matriculeFiscale,
        };
      });
      return changed ? next : prev;
    });
  }, [clientRegistryForInvoice]);

  useEffect(() => {
    try {
      window.localStorage.setItem(NH_OVERVIEW_STORAGE_KEY, JSON.stringify(overviewData));
    } catch (e) {
      console.warn('NH : impossible de sauvegarder la vue d\'ensemble', e);
    }
  }, [overviewData]);

  useEffect(() => {
    // Cabinet actif impose le matricule (séries NH séparées CGM1 / CGM2)
    if (!activeCabinet?.id) return;
    const mat = resolveMatriculeForActiveCabinet(activeCabinet);
    setSelectedMatriculeComptable(mat);
  }, [
    activeCabinet?.id,
    activeCabinet?.vat_code,
    activeCabinet?.code,
    activeCabinet?.name,
    activeCabinet?.is_default,
  ]);

  useEffect(() => {
    if (!location.state?.pickMatricule) return;
    const mat = resolveMatriculeForActiveCabinet(activeCabinet);
    setSelectedMatriculeComptable(mat);
    setActiveMenu('add');
    setOverviewYearsExpanded(false);
    setAddYearsExpanded(false);
    navigate('/nh', { replace: true, state: {} });
  }, [location.state?.pickMatricule, navigate, activeCabinet]);

  useEffect(() => {
    try {
      if (selectedMatriculeComptable) {
        window.localStorage.setItem(NH_MATRICULE_STORAGE_KEY, selectedMatriculeComptable);
      } else {
        window.localStorage.removeItem(NH_MATRICULE_STORAGE_KEY);
        window.localStorage.removeItem(NH_ACTIVE_MENU_STORAGE_KEY);
      }
    } catch (e) {
      console.warn('NH : impossible de sauvegarder le matricule', e);
    }
  }, [selectedMatriculeComptable]);

  useEffect(() => {
    if (!selectedMatriculeComptable) return;
    try {
      if (NH_ALLOWED_MENUS.has(activeMenu)) {
        window.localStorage.setItem(NH_ACTIVE_MENU_STORAGE_KEY, activeMenu);
      }
    } catch (e) {
      console.warn('NH : impossible de sauvegarder le menu actif', e);
    }
  }, [activeMenu, selectedMatriculeComptable]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedOverviewItem(null);
  }, [selectedMatriculeComptable, overviewSearchQuery, overviewSelectedYear]);

  useEffect(() => {
    setNhMobileNavOpen(false);
  }, [selectedMatriculeComptable]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = () => {
      if (!mq.matches) setNhMobileNavOpen(false);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!nhMobileNavOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setNhMobileNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nhMobileNavOpen]);

  useEffect(() => {
    if (!selectedMatriculeComptable) return undefined;
    let cancelled = false;

    (async () => {
      setOverviewLoading(true);
      try {
        const notesUrl = selectedMatriculeComptable
          ? `/api/nh-notes?matricule=${encodeURIComponent(selectedMatriculeComptable)}`
          : '/api/nh-notes';
        const notesRes = await fetch(notesUrl);
        if (cancelled) return;

        const notesData = await notesRes.json();
        if (notesData.success) {
          setOverviewData((prev) =>
            mergeNhOverviewNotes(notesData.notes, prev, selectedMatriculeComptable)
          );
        }
      } catch (error) {
        console.warn('NH : chargement vue d\'ensemble', error);
      } finally {
        if (!cancelled) setOverviewLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedMatriculeComptable]);

  // --- LOGIQUE AUTOMATIQUE ---
  useEffect(() => {
    if (isToday) {
      const today = nhTodayIsoLocal();
      if (!nhIsDateAllowedForNewNote(today, lastNoteCreationDateIso)) {
        alertDateBeforeLastNote(lastNoteCreationDateIso);
        setIsToday(false);
        setFormData((prev) => ({ ...prev, dateNH: lastNoteCreationDateIso || prev.dateNH }));
        return;
      }
      setFormData((prev) => ({ ...prev, dateNH: today }));
    }
  }, [isToday, lastNoteCreationDateIso, alertDateBeforeLastNote]);

  useEffect(() => {
    if (!isAutoNumber) {
      setFormData((prev) => ({ ...prev, numeroNH: '' }));
      return;
    }
    const hasDate = isToday || String(formData.dateNH || '').trim() !== '';
    if (!hasDate) {
      setFormData((prev) => ({ ...prev, numeroNH: '' }));
      return;
    }
    const d = (formData.dateNH || '').trim() || new Date().toISOString().slice(0, 10);
    let cancelled = false;
    (async () => {
      const serverMax = await fetchNhDbMaxSeqForDate(d, selectedMatriculeComptable, activeCabinet?.id);
      if (cancelled) return;
      const full = nextNhAutoNumeroAfterMerge(d, overviewData, serverMax, selectedMatriculeComptable);
      setFormData((prev) => ({ ...prev, numeroNH: nhBodyWithoutPrefix(full) }));
    })();
    return () => {
      cancelled = true;
    };
  }, [isAutoNumber, isToday, formData.dateNH, overviewData, selectedMatriculeComptable, activeCabinet?.id]);

  // --- LOGIQUE TABLEAU DYNAMIQUE ---
  const addItem = () => {
    setItems([...items, { id: Date.now(), designation: buildAssistanceDesignation(selectedClientHonoPeriode), tauxTva: 19, puHtv: '', puTtc: 0 }]);
  };

  const removeItem = (id) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };

  const handleItemChange = (id, field, value) => {
    const fp = fiscalData[selectedYear] || fiscalData[2024];
    const newItems = items.map((item) => {
      if (item.id !== id) return item;
      const updatedItem = { ...item, [field]: value };
      const tva = parseFloat(field === 'tauxTva' ? value : item.tauxTva) || 0;
      if (field === 'puHtv') {
        const ht = parseFloat(String(value).replace(',', '.')) || 0;
        updatedItem.puTtc = (ht * (1 + tva / 100)).toFixed(3);
      } else if (field === 'tauxTva') {
        const ht = parseFloat(String(item.puHtv).replace(',', '.')) || 0;
        const tvaVal = parseFloat(String(value).replace(',', '.')) || 0;
        updatedItem.puTtc = (ht * (1 + tvaVal / 100)).toFixed(3);
      } else if (field === 'puTtc') {
        const ttc = parseFloat(String(value).replace(',', '.')) || 0;
        const denom = 1 + tva / 100;
        const ht = denom > 0 ? ttc / denom : 0;
        updatedItem.puHtv = ht === 0 ? '' : String(ht);
        updatedItem.puTtc = ttc === 0 ? '0' : ttc.toFixed(3);
      }
      return updatedItem;
    });
    setItems(newItems);
    if (field === 'puHtv' || field === 'puTtc' || field === 'tauxTva') {
      const validLines = newItems
        .map((it) => ({
          tauxTva: it.tauxTva,
          puHtv: parseFloat(String(it.puHtv ?? '0').replace(',', '.')) || 0,
        }))
        .filter((l) => l.puHtv > 0);
      if (validLines.length > 1) {
        // Plusieurs lignes : le « Montant d'hono » affiche le net total (somme des PU HTVA).
        const t = computeNoteTotalsFromLinesNH(validLines, fp);
        setHonoNetAPayer(t.netAPayer > 0 ? t.netAPayer.toFixed(3) : '');
      } else {
        const first = newItems[0];
        if (first) {
          const tvaN = parseFloat(String(first.tauxTva).replace(',', '.')) || 0;
          const fwd = computeTotalsFromLineHTNH(first.puHtv, `${tvaN}%`, fp);
          setHonoNetAPayer(fwd.netAPayer > 0 ? fwd.netAPayer.toFixed(3) : '');
        }
      }
    }
  };

  const handleHonoNetChange = (value) => {
    setHonoNetAPayer(value);
    const net = parseFloat(String(value).replace(/\s/g, '').replace(',', '.'));
    const fp = fiscalData[selectedYear] || fiscalData[2024];
    setItems((prev) => {
      if (!prev.length) return prev;
      const first = prev[0];
      if (!Number.isFinite(net) || net <= 0) {
        const nextFirst = { ...first, puHtv: '', puTtc: 0 };
        if (first.puHtv === '' && (first.puTtc === 0 || first.puTtc === '0')) return prev;
        return [nextFirst, ...prev.slice(1)];
      }
      const tvaN = parseFloat(String(first.tauxTva).replace(',', '.')) || 0;
      const inv = computeLinePuFromNetAPayer(net, tvaN, fp);
      if (first.puHtv === inv.puHtv && String(first.puTtc) === inv.puTtc) return prev;
      return [{ ...first, puHtv: inv.puHtv, puTtc: inv.puTtc }, ...prev.slice(1)];
    });
  };

  useEffect(() => {
    const net = parseFloat(String(honoNetAPayerRef.current).replace(/\s/g, '').replace(',', '.'));
    if (!Number.isFinite(net) || net <= 0) return;
    const fp = fiscalData[selectedYear] || fiscalData[2024];
    setItems((prev) => {
      if (!prev.length) return prev;
      const first = prev[0];
      const tvaN = parseFloat(String(first.tauxTva).replace(',', '.')) || 0;
      const inv = computeLinePuFromNetAPayer(net, tvaN, fp);
      if (first.puHtv === inv.puHtv && String(first.puTtc) === inv.puTtc) return prev;
      return [{ ...first, puHtv: inv.puHtv, puTtc: inv.puTtc }, ...prev.slice(1)];
    });
  }, [selectedYear, fiscalData]);

  const getDesignationDateParts = () => {
    const baseDate = formData.dateNH ? new Date(`${formData.dateNH}T00:00:00`) : new Date();
    const monthIndex = Number.isNaN(baseDate.getTime()) ? new Date().getMonth() : baseDate.getMonth();
    return {
      monthIndex,
      year: selectedYear
    };
  };

  const buildAssistanceDesignation = (periode) => {
    const { monthIndex, year } = getDesignationDateParts();
    const monthNames = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    const quarter = Math.floor(monthIndex / 3) + 1;
    const semester = monthIndex < 6 ? 1 : 2;

    if (periode === 'mensuel') {
      return `Assistance Comptable Mois ${monthNames[monthIndex]} ${year}`;
    }
    if (periode === 'trimestriel') {
      return `Assistance Comptable ${quarter === 1 ? '1er' : `${quarter}ème`} Trimestre ${year}`;
    }
    if (periode === 'semestriel') {
      return `Assistance Comptable ${semester === 1 ? '1er' : '2ème'} Semestre ${year}`;
    }
    if (periode === 'annuel') {
      return `Assistance Comptable Année ${year}`;
    }
    return '';
  };

  const getClientHonoPeriode = (clientId) => {
    const periodes = honoPeriodeParClient[clientId] || {};
    for (const periode of ['mensuel', 'trimestriel', 'semestriel', 'annuel']) {
      if (periodes[periode]) return periode;
    }
    return '';
  };

  const getClientHonoMontant = (clientId, periode) => {
    const periodes = honoPeriodeParClient[clientId] || {};
    const row = periodes[periode] || Object.values(periodes)[0];
    const montant = parseFloat(String(row?.montant ?? 0).replace(',', '.'));
    return Number.isNaN(montant) ? 0 : montant;
  };

  const formatMontantTnd = (value) => {
    const amount = parseFloat(value || 0);
    return amount.toLocaleString('fr-FR', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    });
  };

  // --- DONNÉES & FONCTIONS UTILES ---
  const menuOptions = [
    { id: 'add', label: 'Ajouter Une Note', icon: 'fa-plus' },
    { id: 'overview', label: 'Vue d\'Ensemble', icon: 'fa-chart-pie' },
    { id: 'search', label: 'Chercher Une Note', icon: 'fa-search' }
  ];

  const societies = nhClients.map(client => ({
    id: client.id,
    nom: getClientNomComplet(client),
    matricule: client.matricule_fiscal || '',
    adresse: client.adresse || '',
    email: client.email || '',
    username: client.username,
    personneType: String(client.personne_type || '').toUpperCase(),
    nhType: String(client.nh_type || 'E').toUpperCase()
  }));

  const getCurrentFiscalData = () => fiscalData[selectedYear] || fiscalData[2024];
  const currentFiscalParams = getCurrentFiscalData();
  const retenueSourceTaux = currentFiscalParams.retenue;
  const timbreFiscal = currentFiscalParams.timbre;
  const tvaCollectee0Taux = currentFiscalParams.tva0;
  const tvaCollectee19Taux = currentFiscalParams.tva19;
  const fiscalDisplayValues = {
    retenue: `${retenueSourceTaux}%`,
    timbre: `${timbreFiscal.toFixed(3).replace('.', ',')} Dt`,
    tva0: `${tvaCollectee0Taux}%`,
    tva19: `${tvaCollectee19Taux}%`
  };
  const fiscalYears = Object.keys(fiscalData)
    .map(Number)
    .sort((a, b) => b - a);

  const billingMonthFromDate = nhParseYearMonthFromIsoDate(formData.dateNH);
  const activeBillingMonthIndex =
    billingMonthFromDate && billingMonthFromDate.year === selectedYear
      ? billingMonthFromDate.monthIndex
      : null;

  const handleSelectBillingMonth = (year, monthIndex) => {
    const iso = nhLastDayIsoOfMonth(year, monthIndex);
    if (!nhIsDateAllowedForNewNote(iso, lastNoteCreationDateIso)) {
      alertDateBeforeLastNote(lastNoteCreationDateIso);
      return;
    }
    setIsToday(false);
    setFormData((prev) => ({ ...prev, dateNH: iso }));
  };

  const handleNewFiscalChange = (field, value) => {
    setNewFiscalParams(prev => ({ ...prev, [field]: value }));
  };

  const handleAddFiscalYear = (e) => {
    if (!userIsAdmin) return;
    e.preventDefault();
    const year = Number(newFiscalParams.annee);
    if (!year) return;

    const fiscalParams = {
      retenue: parseFloat(String(newFiscalParams.retenue || '0').replace(',', '.')) || 0,
      timbre: parseFloat(String(newFiscalParams.timbre || '0').replace(',', '.')) || 0,
      tva0: parseFloat(String(newFiscalParams.tva0 || '0').replace(',', '.')) || 0,
      tva19: parseFloat(String(newFiscalParams.tva19 || '0').replace(',', '.')) || 0
    };

    setFiscalData(prev => ({
      ...prev,
      [year]: fiscalParams
    }));
    setSelectedYear(year);
    setNewFiscalParams({ annee: '', retenue: '', timbre: '', tva0: '', tva19: '' });
    setShowFiscalForm(false);
  };

  const handleDeleteFiscalYear = () => {
    if (!userIsAdmin) return;
    const yearToDelete = selectedYear;
    const remainingYears = fiscalYears.filter(year => year !== yearToDelete);
    if (remainingYears.length === 0) return;
    if (!window.confirm(`Vous êtes sûr de supprimer cette année ${yearToDelete} ?`)) return;

    setFiscalData(prev => {
      const next = { ...prev };
      delete next[yearToDelete];
      return next;
    });
    setSelectedYear(remainingYears[0]);
  };

  const filteredSocieties = societies.filter((s) => {
    const search = searchTerm.toLowerCase();
    if (!search) return true;
    return (
      s.nom.toLowerCase().includes(search) ||
      s.matricule.toLowerCase().includes(search) ||
      String(s.username || '').toLowerCase().includes(search)
    );
  });

  const handleSelectSociety = (s) => {
    const clientHonoPeriode = getClientHonoPeriode(s.id) || 'mensuel';
    const clientHonoMontant = getClientHonoMontant(s.id, clientHonoPeriode);
    const designation = buildAssistanceDesignation(clientHonoPeriode);
    setSelectedClientId(s.id);
    setSelectedClientHonoPeriode(clientHonoPeriode);
    setSelectedClientHonoMontant(clientHonoMontant);
    setNatureContrat(s.nhType === 'NH' ? 'CONVENTION' : 'PASSAGER');
    setFormData(prev => ({
      ...prev,
      societe: s.nom || '',
      codeTva: s.matricule || '',
      adresse: s.adresse || '',
      email: s.email || ''
    }));
    const fp = fiscalData[selectedYear] || fiscalData[2024];
    if (clientHonoMontant > 0) {
      setHonoNetAPayer(clientHonoMontant.toFixed(3));
    } else {
      setHonoNetAPayer('');
    }
    setItems(prev => {
      const nextItems = prev.length > 0 ? [...prev] : [{ id: Date.now(), designation: '', tauxTva: 19, puHtv: '', puTtc: 0 }];
      const base = {
        ...nextItems[0],
        designation,
        puHtv: '',
        puTtc: 0
      };
      let first = base;
      if (clientHonoMontant > 0) {
        const tvaN = parseFloat(String(base.tauxTva).replace(',', '.')) || 0;
        const inv = computeLinePuFromNetAPayer(clientHonoMontant, tvaN, fp);
        first = { ...base, puHtv: inv.puHtv, puTtc: inv.puTtc };
      }
      nextItems[0] = first;
      return nextItems;
    });
    setShowModal(false);
  };

  useEffect(() => {
    if (!selectedClientHonoPeriode || !formData.societe) return;
    const designation = buildAssistanceDesignation(selectedClientHonoPeriode);
    setItems(prev => {
      if (!prev.length) return [{ id: Date.now(), designation, tauxTva: 19, puHtv: '', puTtc: 0 }];
      return prev.map((item, index) => {
        if (index !== 0) return item;
        const canAutoUpdate = !item.designation || item.designation.startsWith('Assistance Comptable');
        return canAutoUpdate ? { ...item, designation } : item;
      });
    });
  }, [selectedClientHonoPeriode, selectedYear, formData.dateNH, formData.societe]);

  const hasDateForAutoNumber = isToday || String(formData.dateNH || '').trim() !== '';

  // --- RENDU DU CONTENU DYNAMIQUE ---
  const renderContent = () => {
    switch (activeMenu) {
      case 'add':
        return (
          <div className="nh-content nh-content--compact nh-content--add">
            <div className="cgm-page-head nh-add-page-head">
              <div className="cgm-page-title-wrap">
                <span className="cgm-page-title">Ajouter une note</span>
              </div>
              <div className="nh-add-matricule-wrap">
                <span className="nh-add-matricule-label">Matricule comptable</span>
                <span className="cgm-count-badge">{selectedMatriculeComptable}</span>
              </div>
            </div>

            <div className="nh-modern-form-card">
              <div className="nh-modern-form-card__title">Paramètres fiscaux</div>
              <div className="fiscal-years fiscal-years--stack">
                <div className="fiscal-params-toolbar">
                  <div className="fiscal-months-picker-wrap">
                    <button
                      type="button"
                      className={`nh-month-picker-trigger${monthsMenuOpen ? ' is-open' : ''}`}
                      onClick={() => setMonthsMenuOpen((open) => !open)}
                      aria-expanded={monthsMenuOpen}
                      aria-controls="nh-billing-period-panel"
                    >
                      <span className="nh-month-picker-trigger-icon" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                      </span>
                      <span className="nh-month-picker-trigger-text">
                        <span className="nh-month-picker-label">Sélectionner mois</span>
                        <span className="nh-month-picker-value">
                          {activeBillingMonthIndex != null
                            ? `${NH_MONTH_LABELS_FULL[activeBillingMonthIndex]} ${selectedYear}`
                            : `Choisir une période · ${selectedYear}`}
                        </span>
                      </span>
                      <span className="nh-month-picker-chevron" aria-hidden="true" />
                    </button>
                    {monthsMenuOpen && (
                      <div
                        id="nh-billing-period-panel"
                        className="nh-month-sidebar-ui nh-billing-period--revealed"
                        role="listbox"
                        aria-label={`Mois ${selectedYear}`}
                      >
                        <div className="nh-month-sidebar">
                          {NH_MONTH_LABELS_FULL.map((monthName, monthIndex) => {
                            const isSelected = activeBillingMonthIndex === monthIndex;
                            return (
                              <button
                                key={monthIndex}
                                type="button"
                                role="option"
                                aria-selected={isSelected}
                                className={`nh-month-vtab${isSelected ? ' active' : ''}`}
                                onClick={() => {
                                  handleSelectBillingMonth(selectedYear, monthIndex);
                                  setMonthsMenuOpen(false);
                                }}
                              >
                                <span className="nh-month-vtab-label">{monthName}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  {userIsAdmin ? (
                    <div className="fiscal-year-toolbar-actions nh-add-fiscal-actions">
                      <button
                        type="button"
                        className="cgm-toolbar-btn cgm-toolbar-btn--add"
                        onClick={() => setShowFiscalForm((prev) => !prev)}
                        title="Ajouter une année fiscale"
                      >
                        <i className="fas fa-plus" aria-hidden="true" />
                        Ajouter année
                      </button>
                      <button
                        type="button"
                        className="cgm-toolbar-btn cgm-toolbar-btn--delete"
                        onClick={handleDeleteFiscalYear}
                        disabled={fiscalYears.length <= 1}
                        title="Supprimer l'année sélectionnée"
                      >
                        <i className="fas fa-trash" aria-hidden="true" />
                        Supprimer année
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
              {userIsAdmin && showFiscalForm && (
                <form className="fiscal-add-form" onSubmit={handleAddFiscalYear}>
                  <div className="fiscal-add-field">
                    <label>Année</label>
                    <input
                      type="number"
                      value={newFiscalParams.annee}
                      onChange={(e) => handleNewFiscalChange('annee', e.target.value)}
                      placeholder="2025"
                      required
                    />
                  </div>
                  <div className="fiscal-add-field">
                    <label>Retenue à la Source (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newFiscalParams.retenue}
                      onChange={(e) => handleNewFiscalChange('retenue', e.target.value)}
                      placeholder="3"
                    />
                  </div>
                  <div className="fiscal-add-field">
                    <label>Timbre Fiscal</label>
                    <input
                      type="number"
                      step="0.001"
                      value={newFiscalParams.timbre}
                      onChange={(e) => handleNewFiscalChange('timbre', e.target.value)}
                      placeholder="1.000"
                    />
                  </div>
                  <div className="fiscal-add-field">
                    <label>TVA Collectées 0% (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newFiscalParams.tva0}
                      onChange={(e) => handleNewFiscalChange('tva0', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div className="fiscal-add-field">
                    <label>TVA Collectées 19% (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newFiscalParams.tva19}
                      onChange={(e) => handleNewFiscalChange('tva19', e.target.value)}
                      placeholder="19"
                    />
                  </div>
                  <div className="fiscal-add-actions">
                    <button type="submit" className="fiscal-save-btn">Ajouter</button>
                    <button type="button" className="fiscal-cancel-btn" onClick={() => setShowFiscalForm(false)}>Annuler</button>
                  </div>
                </form>
              )}
              <table className="fiscal-table nh-add-fiscal-table">
                <tbody>
                  <tr><td>Retenue à la Source</td><td><span className="nh-badge-amount">{fiscalDisplayValues.retenue}</span></td></tr>
                  <tr><td>Timbre Fiscal</td><td><span className="nh-badge-amount">{fiscalDisplayValues.timbre}</span></td></tr>
                  <tr><td>TVA Collectées 0%</td><td><span className="nh-badge-taux">{fiscalDisplayValues.tva0}</span></td></tr>
                  <tr><td>TVA Collectées 19%</td><td><span className="nh-badge-taux">{fiscalDisplayValues.tva19}</span></td></tr>
                </tbody>
              </table>
            </div>

            <div className="nh-modern-form-card">
              <div className="nh-modern-form-card__title">Entête &amp; partie supérieure</div>
              <div className="nh-form-grid">
                <div className="form-item">
                  <label>Numéro De La Note D'honoraires</label>
                  <div className="nh-input-group">
                    <span className="nh-addon">NH</span>
                    <input
                      type="text"
                      className={`nh-input ${isAutoNumber ? 'light-bg' : ''} ${isAutoNumber && !hasDateForAutoNumber ? 'nh-input-verify-date' : ''}`}
                      value={isAutoNumber && !hasDateForAutoNumber ? '' : normalizeNumeroDisplay(formData.numeroNH)}
                      readOnly={isAutoNumber}
                      placeholder={isAutoNumber && !hasDateForAutoNumber ? 'Vérifier la date !' : undefined}
                      onChange={(e) => setFormData({ ...formData, numeroNH: e.target.value })}
                    />
                  </div>
                  <label className="nh-checkbox-container">
                    <input type="checkbox" checked={isAutoNumber} onChange={(e) => setIsAutoNumber(e.target.checked)} />
                    Numéro automatique
                  </label>
                </div>

                <div className="form-item">
                  <label>Date :</label>
                  <input
                    type="date"
                    className={`nh-input ${isToday ? 'light-bg' : ''}`}
                    value={formData.dateNH}
                    readOnly={isToday}
                    min={lastNoteCreationDateIso || undefined}
                    onChange={(e) => handleAddFormDateChange(e.target.value)}
                  />
                  {lastNoteCreationDateIso ? (
                    <span className="nh-date-min-hint">
                      Date min. = dernière facture : {formatNhDisplayDate(lastNoteCreationDateIso)}
                    </span>
                  ) : null}
                  <label className="nh-checkbox-container">
                    <input
                      type="checkbox"
                      checked={isToday}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (checked) {
                          const today = nhTodayIsoLocal();
                          if (!nhIsDateAllowedForNewNote(today, lastNoteCreationDateIso)) {
                            alertDateBeforeLastNote(lastNoteCreationDateIso);
                            return;
                          }
                        }
                        setIsToday(checked);
                      }}
                    />
                    Aujourd&apos;hui
                  </label>
                </div>

                <div className="form-item"><label>Société</label><input type="text" value={formData.societe} className="nh-input light-bg" readOnly /></div>
                <div className="form-item"><label>Adresse</label><input type="text" value={formData.adresse} className="nh-input light-bg" readOnly /></div>
                <div className="form-item"><label>Code TVA</label><input type="text" value={formData.codeTva} className="nh-input light-bg" readOnly /></div>
                <div className="form-item">
                  <label>Type</label>
                  <div className="nh-radio-group">
                    <label className={`nh-radio-label${natureContrat === 'CONVENTION' ? ' nh-radio-active' : ''}`}>
                      <input type="radio" name="natureContrat" value="CONVENTION" checked={natureContrat === 'CONVENTION'} onChange={() => setNatureContrat('CONVENTION')} />
                      CONVENTION
                    </label>
                    <label className={`nh-radio-label${natureContrat === 'PASSAGER' ? ' nh-radio-active' : ''}`}>
                      <input type="radio" name="natureContrat" value="PASSAGER" checked={natureContrat === 'PASSAGER'} onChange={() => setNatureContrat('PASSAGER')} />
                      PASSAGER
                    </label>
                  </div>
                </div>
                <div className="form-actions nh-add-form-actions">
                  <button type="button" className="cgm-toolbar-btn cgm-toolbar-btn--primary" onClick={() => setShowModal(true)}>
                    <i className="fas fa-search" aria-hidden="true" />
                    Chercher société
                  </button>
                  <button
                    type="button"
                    className="cgm-toolbar-btn cgm-toolbar-btn--reset"
                    onClick={() => { setFormData({ numeroNH: '', dateNH: nhDefaultDateNHForAddForm(fiscalData), societe: '', adresse: '', codeTva: '', email: '' }); setSelectedYear(nhDefaultSelectedYearForFiscal(fiscalData)); setSelectedClientId(null); setSelectedClientHonoPeriode(''); setSelectedClientHonoMontant(0); setHonoNetAPayer(''); setItems([{ id: Date.now(), designation: '', tauxTva: 19, puHtv: '', puTtc: 0 }]); setIsAutoNumber(false); setIsToday(false); setNatureContrat('CONVENTION'); }}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>

            <div className="nh-modern-form-card nh-modern-form-card--lines">
              <div className="nh-modern-form-card__title">Description &amp; prix</div>
              <div className="nh-form-grid nh-hono-net-grid">
                <div className="form-item">
                  <label htmlFor="nh-hono-net">Montant d&apos;hono (net à payer)</label>
                  <input
                    id="nh-hono-net"
                    type="text"
                    className="nh-input"
                    inputMode="decimal"
                    autoComplete="off"
                    value={honoNetAPayer}
                    onChange={(e) => handleHonoNetChange(e.target.value)}
                  />
                </div>
              </div>
              <div className="nh-modern-table-card nh-add-lines-card">
                <div className="nh-modern-table-toolbar nh-add-lines-toolbar">
                  <span className="nh-add-lines-label">Tâches de la NH</span>
                  <button type="button" className="cgm-toolbar-btn cgm-toolbar-btn--primary nh-add-line-btn" onClick={addItem}>
                    <i className="fas fa-plus" aria-hidden="true" />
                    Ajouter une ligne
                  </button>
                </div>
                <div className="overview-table-container nh-modern-table-scroll">
                  <table className="items-table nh-modern-data-table nh-add-items-table">
                    <thead>
                      <tr>
                        <th><span className="nh-modern-th-inner">#</span></th>
                        <th><span className="nh-modern-th-inner">Désignation</span></th>
                        <th><span className="nh-modern-th-inner">Taux TVA</span></th>
                        <th><span className="nh-modern-th-inner">PU HTVA</span></th>
                        <th><span className="nh-modern-th-inner">PU TTC</span></th>
                        <th><span className="nh-modern-th-inner">Action</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => (
                        <tr key={item.id}>
                          <td>{index + 1}</td>
                          <td><input type="text" className="nh-add-table-input" value={item.designation} onChange={(e) => handleItemChange(item.id, 'designation', e.target.value)} /></td>
                          <td>
                            <select className="nh-add-table-input nh-add-table-select" value={item.tauxTva} onChange={(e) => handleItemChange(item.id, 'tauxTva', e.target.value)}>
                              <option value="19">19%</option><option value="13">13%</option><option value="7">7%</option><option value="0">0%</option>
                            </select>
                          </td>
                          <td><input type="number" className="nh-add-table-input" value={item.puHtv} onChange={(e) => handleItemChange(item.id, 'puHtv', e.target.value)} step="0.001" /></td>
                          <td className="nh-pu-ttc-cell">
                            <input
                              type="number"
                              className="nh-add-table-input nh-pu-ttc-input"
                              value={item.puTtc}
                              onChange={(e) => handleItemChange(item.id, 'puTtc', e.target.value)}
                              step="0.001"
                              placeholder="0"
                            />
                          </td>
                          <td className="text-center">
                            <button type="button" className="nh-action-icon-btn nh-action-icon-btn--delete" onClick={() => removeItem(item.id)} title="Supprimer la ligne">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                <line x1="10" y1="11" x2="10" y2="17" />
                                <line x1="14" y1="11" x2="14" y2="17" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="final-actions nh-add-final-actions">
                <button type="button" className="cgm-toolbar-btn cgm-toolbar-btn--duplicate nh-add-submit-btn" onClick={handleAddNote}>
                  <i className="fas fa-database" aria-hidden="true" />
                  Ajouter la note
                </button>
                <button type="button" className="cgm-toolbar-btn cgm-toolbar-btn--reset" onClick={() => { setHonoNetAPayer(''); setItems([{ id: Date.now(), designation: '', tauxTva: 19, puHtv: '', puTtc: 0 }]); }}>
                  <i className="fas fa-undo" aria-hidden="true" />
                  Réinitialiser
                </button>
              </div>
            </div>
          </div>
        );

      case 'overview':
        return (
          <div className="nh-content nh-content--compact nh-content--overview">
            <h2 className="main-title">
              Vue d&apos;Ensemble
              {overviewSelectedYear ? ` | ${overviewSelectedYear}` : ' | Toutes les années'}
            </h2>
            <div className="nh-section overview-table-section">
              <div className="cgm-list-toolbar">
                <div className="cgm-page-head">
                  <div className="cgm-page-title-wrap">
                    <span className="cgm-page-title">Notes d&apos;honoraires</span>
                    <span className="cgm-count-badge" title={`${totalItems} note${totalItems !== 1 ? 's' : ''}`}>
                      {totalItems}
                    </span>
                  </div>
                  <div className="cgm-toolbar cgm-toolbar--export">
                    <button type="button" className="cgm-toolbar-btn cgm-toolbar-btn--excel" onClick={handleExportExcel}>
                      <i className="fas fa-file-excel" aria-hidden="true" />
                      Excel
                    </button>
                    <button type="button" className="cgm-toolbar-btn cgm-toolbar-btn--pdf" onClick={handleExportPDF}>
                      <i className="fas fa-file-pdf" aria-hidden="true" />
                      PDF
                    </button>
                    <button type="button" className="cgm-toolbar-btn cgm-toolbar-btn--print" onClick={handlePrint}>
                      <i className="fas fa-print" aria-hidden="true" />
                      Imprimer
                    </button>
                  </div>
                </div>

                <div className="cgm-filter-panel">
                  <div className="cgm-filter-panel__title">Critères de recherche</div>
                  <div className="cgm-filter-row">
                    <div className="cgm-filter-field">
                      <label htmlFor="nh-overview-year-select">Année</label>
                      <select
                        id="nh-overview-year-select"
                        value={overviewSelectedYear === '' || overviewSelectedYear == null ? '' : overviewSelectedYear}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '') {
                            setOverviewSelectedYear('');
                            setCurrentPage(1);
                            return;
                          }
                          handleOverviewYearSelect(Number(v));
                        }}
                      >
                        <option value="">Toutes les années</option>
                        {fiscalYears.map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                    <div className="cgm-filter-field">
                      <label htmlFor="nh-overview-month-select">Mois</label>
                      <select
                        id="nh-overview-month-select"
                        value={overviewSelectedMonth}
                        onChange={(e) =>
                          setOverviewSelectedMonth(e.target.value === '' ? '' : Number(e.target.value))
                        }
                      >
                        <option value="">Tous les mois</option>
                        {NH_MONTH_LABELS_FULL.map((label, idx) => (
                          <option key={idx} value={idx}>{label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="cgm-filter-field">
                      <label htmlFor="nh-overview-trimestre-select">Trimestre</label>
                      <select
                        id="nh-overview-trimestre-select"
                        value={overviewSelectedTrimestre}
                        onChange={(e) =>
                          setOverviewSelectedTrimestre(
                            e.target.value === '' ? '' : Number(e.target.value)
                          )
                        }
                      >
                        <option value="">Tous les trimestres</option>
                        <option value={1}>1er trimestre</option>
                        <option value={2}>2ème trimestre</option>
                        <option value={3}>3ème trimestre</option>
                        <option value={4}>4ème trimestre</option>
                      </select>
                    </div>
                    <div className="cgm-filter-field">
                      <label htmlFor="nh-overview-semestre-select">Semestre</label>
                      <select
                        id="nh-overview-semestre-select"
                        value={overviewSelectedSemestre}
                        onChange={(e) =>
                          setOverviewSelectedSemestre(
                            e.target.value === '' ? '' : Number(e.target.value)
                          )
                        }
                      >
                        <option value="">Tous les semestres</option>
                        <option value={1}>1er semestre</option>
                        <option value={2}>2ème semestre</option>
                      </select>
                    </div>
                    <div className="cgm-filter-search">
                      <label className="cgm-sr-only" htmlFor="nh-overview-search-input">
                        Rechercher une note
                      </label>
                      <div className="cgm-filter-search__box" role="search">
                        <div className="cgm-filter-search__icon" aria-hidden="true">
                          <svg viewBox="0 0 20 20">
                            <path d="M16.72 17.78a.75.75 0 1 0 1.06-1.06l-1.06 1.06ZM9 14.5A5.5 5.5 0 0 1 3.5 9H2a7 7 0 0 0 7 7v-1.5ZM3.5 9A5.5 5.5 0 0 1 9 3.5V2a7 7 0 0 0-7 7h1.5ZM9 3.5A5.5 5.5 0 0 1 14.5 9H16a7 7 0 0 0-7-7v1.5Zm3.89 10.45 3.83 3.83 1.06-1.06-3.83-3.83-1.06 1.06ZM14.5 9a5.48 5.48 0 0 1-1.61 3.89l1.06 1.06A6.98 6.98 0 0 0 16 9h-1.5Zm-1.61 3.89A5.48 5.48 0 0 1 9 14.5V16a6.98 6.98 0 0 0 4.95-2.05l-1.06-1.06Z" />
                          </svg>
                        </div>
                        <input
                          id="nh-overview-search-input"
                          type="search"
                          className="cgm-filter-search__input"
                          placeholder="N° note, société, date, montant…"
                          value={overviewSearchQuery}
                          onChange={(e) => setOverviewSearchQuery(e.target.value)}
                          autoComplete="off"
                          aria-label="Rechercher une note d'honoraires"
                        />
                        {overviewSearchQuery ? (
                          <button
                            type="button"
                            className="cgm-filter-search__clear"
                            onClick={() => setOverviewSearchQuery('')}
                            aria-label="Effacer la recherche"
                          >
                            <i className="fas fa-times" aria-hidden="true" />
                          </button>
                        ) : null}
                        <button type="button" className="cgm-filter-search__submit" tabIndex={-1}>
                          Filtrer
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="cgm-toolbar cgm-toolbar--row-actions">
                  <button
                    type="button"
                    className="cgm-toolbar-btn cgm-toolbar-btn--duplicate"
                    disabled={!selectedOverviewItem}
                    onClick={(e) => { if (selectedOverviewItem) { handleDuplicateNoteFromRow(selectedOverviewItem, e); setSelectedOverviewItem(null); } }}
                    title="Dupliquer la note"
                  >
                    <i className="fas fa-copy" aria-hidden="true" />
                    Dupliquer
                  </button>
                </div>
              </div>

              <div className="nh-modern-table-card">
                <div className="nh-modern-table-toolbar">
                  <label className="nh-modern-table-length" htmlFor="nh-overview-page-size">
                    Afficher
                    <select
                      id="nh-overview-page-size"
                      value={itemsPerPage}
                      onChange={(e) => setItemsPerPage(Number(e.target.value))}
                    >
                      <option value={6}>6</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    lignes
                  </label>
                </div>

                <div className="overview-table-container nh-modern-table-scroll">
                  <table className="overview-table nh-overview-data-table nh-modern-data-table">
                    <thead>
                      <tr>
                        {columns.map(column => (
                          columnVisibility[column.key] && (
                            <th
                              key={column.key}
                              className={`nh-col-${column.key}${column.key === 'actions'
                                ? ' nh-overview-actions-cell nh-overview-actions-merged'
                                : ''
                                }`}
                            >
                              <span className="nh-modern-th-inner">
                                {column.label}
                                {column.key !== 'actions' ? (
                                  <span className="nh-modern-sort-icons" aria-hidden="true">
                                    <i className="fas fa-caret-up" />
                                    <i className="fas fa-caret-down" />
                                  </span>
                                ) : null}
                              </span>
                            </th>
                          )
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.length > 0 ? (
                        currentItems.map((item) => (
                          <tr
                            key={item.id}
                            className={`clickable-row${selectedOverviewItem?.id === item.id ? ' nh-row-selected' : ''}`}
                            onClick={() => setSelectedOverviewItem(selectedOverviewItem?.id === item.id ? null : item)}
                            onDoubleClick={() => handleViewInvoice(item)}
                          >
                            {columns.map(column => {
                              if (!columnVisibility[column.key]) return null;

                              if (column.key === 'actions') {
                                return (
                                  <td key={column.key} className="nh-col-actions">
                                    <div className="nh-action-icons">
                                      {/* Bouton Voir */}
                                      <button
                                        type="button"
                                        className="nh-action-icon-btn nh-action-icon-btn--view"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleViewInvoice(item);
                                        }}
                                        title="Voir la facture"
                                      >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                          <circle cx="12" cy="12" r="3"></circle>
                                        </svg>
                                      </button>

                                      {userIsAdmin && (
                                      <button
                                        type="button"
                                        className="nh-action-icon-btn nh-action-icon-btn--edit"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleModifyNote(item);
                                        }}
                                        title="Modifier la note"
                                      >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                        </svg>
                                      </button>
                                      )}

                                      {/* Bouton Supprimer */}
                                      <button
                                        type="button"
                                        className="nh-action-icon-btn nh-action-icon-btn--delete"
                                        disabled={!userIsAdmin}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteNote(item);
                                        }}
                                        title="Supprimer la note"
                                      >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <polyline points="3 6 5 6 21 6"></polyline>
                                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                          <line x1="10" y1="11" x2="10" y2="17"></line>
                                          <line x1="14" y1="11" x2="14" y2="17"></line>
                                        </svg>
                                      </button>
                                    </div>
                                  </td>
                                );
                              }
                              let cellContent;
                              if (column.key === 'date') {
                                cellContent = formatNhDisplayDate(item.date, item.numero);
                              } else if (column.key === 'montantHT') {
                                const val = parseFloat(String(item.montantHT ?? '0').replace(',', '.'));
                                cellContent = Number.isFinite(val)
                                  ? (
                                    <span className="nh-badge nh-badge-amount">
                                      {val.toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                                    </span>
                                  )
                                  : (item.montantHT ?? '—');
                              } else if (column.key === 'tauxTaxe') {
                                cellContent = (
                                  <span className="nh-badge nh-badge-taux">{item.tauxTaxe ?? '—'}</span>
                                );
                              } else {
                                cellContent = item[column.key] ?? '—';
                              }

                              const titleAttr =
                                typeof cellContent === 'string' || typeof cellContent === 'number'
                                  ? String(cellContent)
                                  : undefined;

                              return (
                                <td key={column.key} className={`nh-col-${column.key}`} title={titleAttr}>
                                  <span className="nh-cell-truncate">{cellContent}</span>
                                </td>
                              );
                            })}
                          </tr>
                        ))
                      ) : (
                        <tr className="empty-row">
                          <td colSpan={Object.values(columnVisibility).filter(v => v === true).length || 1} className="empty-table-message">
                            <i className="fas fa-info-circle"></i> Aucune note d'honoraires à afficher
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {totalItems > 0 && (
                  <div className="nh-modern-table-footer">

                    <div className="nh-modern-pagination">
                      <button
                        type="button"
                        className="nh-modern-page-nav"
                        onClick={goToPrevPage}
                        disabled={currentPage === 1}
                      >
                        Précédent
                      </button>
                      <div className="nh-modern-page-numbers">
                        {getPageNumbers().map((page, index) => (
                          page === '...' ? (
                            <span key={`dots-${index}`} className="nh-modern-page-dots">…</span>
                          ) : (
                            <button
                              key={`page-${page}`}
                              type="button"
                              className={`nh-modern-page-btn${currentPage === page ? ' is-active' : ''}`}
                              onClick={() => setCurrentPage(page)}
                            >
                              {page}
                            </button>
                          )
                        ))}
                      </div>
                      <button
                        type="button"
                        className="nh-modern-page-nav"
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                      >
                        Suivant
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'search':
        return (
          <div className="nh-content nh-content--compact">
            <h2 className="main-title">Chercher une note</h2>
            <form className="nh-search-form" onSubmit={handleSearchSubmit}>
              <div className="search-form-container">
                <div className="search-form-header">Formulaire de recherche des notes d&apos;honoraires</div>
                <div className="search-form-body">
                  <div className="form-row">
                    <div className="form-group flex-1">
                      <label htmlFor="nh-search-ident">Identification</label>
                      <input
                        id="nh-search-ident"
                        type="text"
                        placeholder="Numéro ou ID…"
                        value={searchIdentification}
                        onChange={(e) => setSearchIdentification(e.target.value)}
                      />
                    </div>
                    <div className="form-group flex-1">
                      <label htmlFor="nh-search-date-note">Date de la note</label>
                      <div className="input-group">
                        <input
                          id="nh-search-date-note"
                          type="date"
                          value={searchDateNote}
                          onChange={(e) => setSearchDateNote(e.target.value)}
                          className="date-input"
                        />
                        <span className="input-icon-box" aria-hidden="true">📅</span>
                      </div>
                    </div>
                    <div className="form-group flex-1">
                      <label htmlFor="nh-search-date-creation">Date de création</label>
                      <div className="input-group">
                        <input
                          id="nh-search-date-creation"
                          type="date"
                          value={searchDateCreation}
                          onChange={(e) => setSearchDateCreation(e.target.value)}
                          className="date-input"
                        />
                        <span className="input-icon-box" aria-hidden="true">📅</span>
                      </div>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group flex-1">
                      <label htmlFor="nh-search-societe">Société</label>
                      <input
                        id="nh-search-societe"
                        type="text"
                        placeholder="Nom ou matricule…"
                        value={searchSociete}
                        onChange={(e) => setSearchSociete(e.target.value)}
                      />
                    </div>
                    <div className="form-group flex-2">
                      <label htmlFor="nh-search-desc">Désignation</label>
                      <input
                        id="nh-search-desc"
                        type="text"
                        value={searchDescriptions}
                        onChange={(e) => setSearchDescriptions(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="form-row align-items-end nh-search-interval-ttc-row">
                    <div className="form-group nh-search-interval-col">
                      <div className="label-with-toggle nh-search-toggle-row">
                        <label>Recherche par intervalle</label>
                        <div className="nh-search-yes-no" role="group" aria-label="Recherche par intervalle de dates">
                          <button
                            type="button"
                            className={`nh-yes-no-btn ${searchIntervalle ? 'is-active yes' : ''}`}
                            aria-pressed={searchIntervalle}
                            onClick={() => setSearchIntervalle(true)}
                          >
                            Oui
                          </button>
                          <button
                            type="button"
                            className={`nh-yes-no-btn ${!searchIntervalle ? 'is-active no' : ''}`}
                            aria-pressed={!searchIntervalle}
                            onClick={() => setSearchIntervalle(false)}
                          >
                            Non
                          </button>
                        </div>
                      </div>
                      <div className={`input-group interval-group ${!searchIntervalle ? 'disabled-group' : ''}`}>
                        <span className="input-icon-box" aria-hidden="true">📅</span>
                        <input
                          type="date"
                          disabled={!searchIntervalle}
                          value={searchIntervalStart}
                          onChange={(e) => setSearchIntervalStart(e.target.value)}
                          aria-label="Date début intervalle"
                        />
                        <span className="input-icon-box" aria-hidden="true">➡️</span>
                        <input
                          type="date"
                          disabled={!searchIntervalle}
                          value={searchIntervalEnd}
                          onChange={(e) => setSearchIntervalEnd(e.target.value)}
                          aria-label="Date fin intervalle"
                        />
                      </div>
                    </div>
                    <div className="form-group nh-search-ttc-col">
                      <label htmlFor="nh-search-ttc">Prix TTC</label>
                      <input
                        id="nh-search-ttc"
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        className="nh-search-ttc-input"

                        value={searchPrixTtc}
                        onChange={(e) => setSearchPrixTtc(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="divider"></div>
                  <div className="form-actions nh-search-form-actions">
                    <button type="submit" className="btn-search nh-search-submit">
                      <i className="fas fa-search me-2" aria-hidden="true" />
                      Lancer la recherche
                    </button>
                    <button type="button" className="btn-reset nh-search-reset" onClick={handleResetSearch}>
                      <i className="fas fa-undo me-2" aria-hidden="true" />
                      Réinitialiser
                    </button>
                  </div>
                </div>
              </div>
            </form>

            {searchResults !== null && (
              <div className="nh-search-results">
                <h3 className="nh-search-results-title">
                  Résultats ({searchResults.length} note{searchResults.length !== 1 ? 's' : ''})
                </h3>
                {searchResults.length === 0 ? (
                  <p className="nh-search-results-empty">
                    <i className="fas fa-info-circle me-2" aria-hidden="true" />
                    Aucune note ne correspond à ces critères pour le matricule sélectionné.
                  </p>
                ) : (
                  <div className="nh-search-results-table-wrap">
                    <table className="overview-table nh-search-results-table">
                      <thead>
                        <tr>
                          <th className="nh-col-numero">Numéro</th>
                          <th>Désignation</th>
                          <th>Montant HT</th>
                          <th>Taux taxe</th>
                          <th className="nh-col-date">Date</th>
                          <th>Société / client</th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchResults.map((item) => (
                          <tr
                            key={item.id}
                            className="clickable-row"
                            onClick={() => handleViewInvoice(item)}
                          >
                            <td className="nh-col-numero">{item.numero}</td>
                            <td>{item.designation}</td>
                            <td>{item.montantHT}</td>
                            <td>{item.tauxTaxe}</td>
                            <td className="nh-col-date">{formatNhDisplayDate(item.date, item.numero)}</td>
                            <td>{item.societe}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="nh-search-results-hint">Cliquez sur une ligne pour ouvrir la note.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="nh-page-root">
      {!selectedMatriculeComptable ? (
        <div className="nh-matricule-selection">
          <button
            type="button"
            className="nh-matricule-selection-back"
            onClick={() => navigate('/')}
            title="Retour à l'accueil"
            aria-label="Retour à l'accueil"
          >
            <i className="fas fa-arrow-left" aria-hidden="true" />
            <span>Retour</span>
          </button>

          <div className="nh-matricule-selection__grid">
            <button
              type="button"
              className="nh-matricule-card nh-matricule-card--pp"
              onClick={() => setSelectedMatriculeComptable('1408941H')}
              aria-label="Matricule 1408941H"
            >
              <span className="nh-matricule-card__code">1408941H</span>
            </button>

            <button
              type="button"
              className="nh-matricule-card nh-matricule-card--pm"
              onClick={() => setSelectedMatriculeComptable('1835448D')}
              aria-label="Matricule 1835448D"
            >
              <span className="nh-matricule-card__code">1835448D</span>
            </button>
          </div>
        </div>
      ) : (
        // Page principale de gestion NH
        <>
          <button
            type="button"
            className={`nh-mobile-nav-toggle${nhMobileNavOpen ? ' nh-mobile-nav-toggle--open' : ''}`}
            onClick={() => setNhMobileNavOpen((o) => !o)}
            aria-expanded={nhMobileNavOpen}
            aria-controls="nh-sidebar-panel"
            title={nhMobileNavOpen ? 'Fermer le menu' : 'Ouvrir le menu Notes d’honoraires'}
            aria-label={nhMobileNavOpen ? 'Fermer le menu NH' : 'Ouvrir le menu NH'}
          >
            <span aria-hidden="true" />
            <span aria-hidden="true" />
            <span aria-hidden="true" />
          </button>
          {nhMobileNavOpen && (
            <div
              className="nh-mobile-nav-overlay"
              role="presentation"
              onClick={() => setNhMobileNavOpen(false)}
              aria-hidden="true"
            />
          )}
          <div className={`nh-page-container${nhMobileNavOpen ? ' nh-mobile-nav-open' : ''}`}>
            <div className="nh-sidebar" id="nh-sidebar-panel">
              <div className="nh-sidebar-inner">
                <nav className="nh-sidebar-nav-card" aria-label="Navigation Notes d&apos;honoraires">
                  <div className="nh-sidebar-brand">
                    <i className="fas fa-home nh-sidebar-brand-icon" aria-hidden="true" />
                    <span>Gestion NH</span>
                  </div>

                  <div className="nh-menu-buttons">
                    <button type="button" className="nh-menu-btn-main" onClick={() => setExpandMenu(!expandMenu)}>
                      <span className="nh-menu-btn-main-left">
                        <i className="fas fa-sticky-note nh-menu-btn-main-icon" aria-hidden="true" />
                        <span>Notes d&apos;Honoraires</span>
                      </span>
                      <i
                        className={`fas fa-chevron-${expandMenu ? 'down' : 'right'} nh-menu-btn-main-chevron`}
                        aria-hidden="true"
                      />
                    </button>
                    {expandMenu && (
                      <div className="nh-submenu">
                        {menuOptions.map((o) => {
                          if (o.id === 'add' || o.id === 'overview') {
                            const isAdd = o.id === 'add';
                            const isActive = activeMenu === o.id;
                            const yearsExpanded = isAdd ? addYearsExpanded : overviewYearsExpanded;
                            const selectedMenuYear = isAdd ? selectedYear : overviewSelectedYear;

                            const onYearSelect = isAdd ? handleAddYearSelect : handleOverviewYearSelect;
                            return (
                              <React.Fragment key={o.id}>
                                <button
                                  type="button"
                                  className={`nh-menu-btn ${isActive ? 'active' : ''}`}
                                  onClick={() => {
                                    if (activeMenu === o.id) {
                                      if (isAdd) {
                                        setAddYearsExpanded((prev) => !prev);
                                      } else {
                                        setOverviewYearsExpanded((prev) => !prev);
                                      }
                                    } else {
                                      setActiveMenu(o.id);
                                      if (isAdd) {
                                        setAddYearsExpanded(true);
                                        setOverviewYearsExpanded(false);
                                      } else {
                                        setOverviewYearsExpanded(true);
                                        setAddYearsExpanded(false);
                                      }
                                    }
                                  }}
                                  aria-expanded={yearsExpanded}
                                >
                                  <span
                                    className={`nh-submenu-dot ${isActive ? 'is-active' : ''}`}
                                    aria-hidden="true"
                                  />
                                  <span className="nh-menu-btn-label">{o.label}</span>
                                  <i
                                    className={`fas fa-chevron-${yearsExpanded ? 'down' : 'right'} nh-menu-btn-inline-chevron`}
                                    aria-hidden="true"
                                  />
                                </button>
                                {yearsExpanded && (
                                  <div className="nh-overview-years-list" role="list">
                                    {fiscalYears.map((year) => (
                                      <button
                                        key={`${o.id}-${year}`}
                                        type="button"
                                        role="listitem"
                                        className={`nh-overview-year-btn${selectedMenuYear === year ? ' active' : ''
                                          }`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onYearSelect(year);
                                          setNhMobileNavOpen(false);
                                        }}
                                      >
                                        <span className="nh-submenu-lead" aria-hidden="true">
                                          ›
                                        </span>
                                        <span>{year}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </React.Fragment>
                            );
                          }
                          return (
                            <button
                              key={o.id}
                              type="button"
                              className={`nh-menu-btn ${activeMenu === o.id ? 'active' : ''}`}
                              onClick={() => {
                                setActiveMenu(o.id);
                                setOverviewYearsExpanded(false);
                                setAddYearsExpanded(false);
                                setNhMobileNavOpen(false);
                              }}
                            >
                              <span
                                className={`nh-submenu-dot ${activeMenu === o.id ? 'is-active' : ''}`}
                                aria-hidden="true"
                              />
                              <span className="nh-menu-btn-label">{o.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </nav>

                <button
                  type="button"
                  className="nh-sidebar-back"
                  onClick={() => {
                    setNhMobileNavOpen(false);
                    navigate('/');
                  }}
                  title="Retour à l'accueil"
                  aria-label="Retour à l'accueil"
                >
                  <i className="fas fa-arrow-left" aria-hidden="true" />
                  <span>Retour</span>
                </button>
              </div>
            </div>
            <div className="nh-main-content" ref={mainContentRef}>{renderContent()}</div>
          </div>

          {nhUiAlert && (
            <div
              className="nh-ui-alert-overlay"
              role="presentation"
              onClick={() => setNhUiAlert(null)}
            >
              <div
                className={`nh-ui-alert-plaque nh-ui-alert-plaque--${nhUiAlert.tone || 'warning'}`}
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="nh-ui-alert-title"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="nh-ui-alert-icon" aria-hidden="true">
                  <i
                    className={`fas ${
                      nhUiAlert.tone === 'danger'
                        ? 'fa-exclamation-triangle'
                        : nhUiAlert.tone === 'success'
                          ? 'fa-check-circle'
                          : 'fa-info-circle'
                    }`}
                  />
                </div>
                <div id="nh-ui-alert-title" className="nh-ui-alert-title">
                  {nhUiAlert.title}
                </div>
                <p className="nh-ui-alert-message">{nhUiAlert.message}</p>
                {nhUiAlert.detail ? (
                  <p className="nh-ui-alert-detail">{nhUiAlert.detail}</p>
                ) : null}
                <button
                  type="button"
                  className="nh-ui-alert-btn"
                  onClick={() => setNhUiAlert(null)}
                >
                  Compris
                </button>
              </div>
            </div>
          )}

          {/* Modal de modification */}
          {userIsAdmin && showEditModal && editingItem && (
            <div className="modal-backdrop">
              <div className="modal-container edit-modal">
                <div className="modal-header"><h3><i className="fas fa-edit"></i> Modifier la Note</h3><button className="close-x" onClick={handleCancelEdit}>&times;</button></div>
                <div className="modal-body">
                  <div className="edit-form">
                    <div className="edit-form-row edit-form-row--meta">
                      <div className="edit-form-group edit-form-group--id">
                        <label>ID</label>
                        <input type="text" className="edit-input readonly-input" value={editingItem.id} readOnly disabled />
                      </div>
                      <div className="edit-form-group">
                        <label>Numéro {canBypassNhDateControl ? '' : '(lecture seule)'}</label>
                        <div className="nh-input-group">
                          <span className="nh-addon">NH</span>
                          <input
                            type="text"
                            className={`edit-input${!canBypassNhDateControl ? ' readonly-input' : ''}`}
                            value={nhBodyWithoutPrefix(editFormData.numero)}
                            readOnly={!canBypassNhDateControl}
                            disabled={!canBypassNhDateControl}
                            onChange={(e) => {
                              if (!canBypassNhDateControl) return;
                              const body = nhBodyWithoutPrefix(e.target.value);
                              setEditFormData({
                                ...editFormData,
                                numero: body ? `NH${body}` : '',
                              });
                            }}
                          />
                        </div>
                        {canBypassNhDateControl ? (
                          <span className="nh-date-min-hint">Admin : numéro modifiable</span>
                        ) : null}
                      </div>
                      <div className="edit-form-group edit-form-group--date">
                        <label>Date {canBypassNhDateControl ? '' : '(lecture seule)'}</label>
                        <input
                          type="date"
                          className={`edit-input${!canBypassNhDateControl ? ' readonly-input' : ''}`}
                          value={editFormData.date}
                          readOnly={!canBypassNhDateControl}
                          disabled={!canBypassNhDateControl}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, date: e.target.value })
                          }
                        />
                        {canBypassNhDateControl ? (
                          <span className="nh-date-min-hint">Admin : modification de date autorisée</span>
                        ) : null}
                      </div>
                      <div className="edit-form-group edit-form-group--client">
                        <label>Société / Client *</label>
                        <input type="text" className="edit-input" value={editFormData.societe} onChange={(e) => setEditFormData({ ...editFormData, societe: e.target.value })} placeholder="Nom de la société" />
                      </div>
                    </div>
                    <div className="edit-form-row edit-form-row--lines">
                      <div className="edit-form-group edit-form-group--full">
                        <div className="items-table-container edit-items-table">
                          <div className="items-table-header">
                            <span>Tâches de la note</span>
                            <button type="button" className="btn-add-line btn-add-line--compact" onClick={addEditItem}>
                              <i className="fas fa-plus" aria-hidden="true" /> Ajouter
                            </button>
                          </div>
                          <table className="items-table">
                            <thead>
                              <tr><th>#</th><th>Désignation</th><th>TVA</th><th>PU HTVA</th><th>PU TTC</th><th></th></tr>
                            </thead>
                            <tbody>
                              {editItems.map((item, index) => (
                                <tr key={item.id}>
                                  <td>{index + 1}</td>
                                  <td>
                                    <input
                                      type="text"
                                      className="table-input"
                                      value={item.designation}
                                      onChange={(e) => handleEditItemChange(item.id, 'designation', e.target.value)}
                                    />
                                  </td>
                                  <td>
                                    <select
                                      className="table-input"
                                      value={item.tauxTva}
                                      onChange={(e) => handleEditItemChange(item.id, 'tauxTva', e.target.value)}
                                    >
                                      <option value="19">19%</option>
                                      <option value="13">13%</option>
                                      <option value="7">7%</option>
                                      <option value="0">0%</option>
                                    </select>
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      className="table-input"
                                      value={item.puHtv}
                                      onChange={(e) => handleEditItemChange(item.id, 'puHtv', e.target.value)}
                                      step="0.001"
                                    />
                                  </td>
                                  <td className="nh-pu-ttc-cell">
                                    <input
                                      type="number"
                                      className="table-input nh-pu-ttc-input"
                                      value={item.puTtc}
                                      onChange={(e) => handleEditItemChange(item.id, 'puTtc', e.target.value)}
                                      step="0.001"
                                    />
                                  </td>
                                  <td className="text-center">
                                    <button type="button" className="btn-delete" onClick={() => removeEditItem(item.id)}>
                                      <i className="fas fa-trash"></i>
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn-cancel-edit" onClick={handleCancelEdit}><i className="fas fa-times" aria-hidden="true" /> Annuler</button>
                  <button className="btn-save-edit" onClick={handleSaveEdit}><i className="fas fa-save" aria-hidden="true" /> Enregistrer</button>
                </div>
              </div>
            </div>
          )}


          {/* Modal de facture */}
          {showInvoiceModal && selectedNoteForInvoice && (
            <div
              className="modal-backdrop"
              onClick={() => {
                setShowInvoiceModal(false);
                setInvoiceDuplicateFormKey(0);
              }}
            >
              <div className="invoice-modal-wrapper" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className="invoice-modal-close"
                  onClick={() => {
                    setShowInvoiceModal(false);
                    setInvoiceDuplicateFormKey(0);
                  }}
                >
                  ×
                </button>
                <Invoice
                  noteData={selectedNoteForInvoice}
                  clientRegistry={clientRegistryForInvoice}
                  onEdit={userIsAdmin ? handleInvoiceEditSave : undefined}
                  onDuplicate={handleInvoiceDuplicate}
                  onDuplicateCancel={() => setInvoiceDuplicateFormKey(0)}
                  duplicateFormKey={invoiceDuplicateFormKey}
                  allInvoices={overviewData}
                  canDeleteNote={userIsAdmin}
                  canEditNote={userIsAdmin}
                  allowFreeNhDate={canBypassNhDateControl}
                />
              </div>
            </div>
          )}

          {showEmailBatchModal && (
            <div className="modal-backdrop" onClick={closeEmailBatchModal}>
              <div
                className="modal-container edit-modal nh-email-batch-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h3>
                    <i className="fas fa-envelope" aria-hidden="true" />
                    Envoyer des factures par e-mail
                  </h3>
                  <button
                    type="button"
                    className="close-x"
                    onClick={closeEmailBatchModal}
                    disabled={emailBatchSending}
                    aria-label="Fermer"
                  >
                    &times;
                  </button>
                </div>
                <div className="modal-body">
                  <div className="nh-email-batch-form">
                    <p className="nh-email-batch-intro">
                      Cochez les factures à envoyer. L&apos;e-mail part vers l&apos;adresse
                      enregistrée sur la fiche client, avec le PDF en pièce jointe.
                    </p>
                    {overviewRowsFiltered.length === 0 ? (
                      <p className="nh-email-batch-empty">Aucune facture à afficher.</p>
                    ) : (
                      <>
                        <label className="nh-email-batch-select-all">
                          <input
                            type="checkbox"
                            checked={emailBatchAllSelected}
                            disabled={emailBatchSending}
                            onChange={(e) => toggleEmailBatchAll(e.target.checked)}
                          />
                          <span>
                            Tout sélectionner ({overviewRowsFiltered.length} facture
                            {overviewRowsFiltered.length > 1 ? 's' : ''})
                          </span>
                        </label>
                        <div className="nh-email-batch-list">
                          {overviewRowsFiltered.map((item) => {
                            const key = getEmailBatchKey(item);
                            const clientEmail = String(item.email || '').trim();
                            return (
                              <label
                                key={key}
                                className={`nh-email-batch-row${emailBatchSelected[key] ? ' is-selected' : ''
                                  }${!clientEmail ? ' no-email' : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={!!emailBatchSelected[key]}
                                  disabled={emailBatchSending}
                                  onChange={() => toggleEmailBatchItem(key)}
                                />
                                <span className="nh-email-batch-row-main">
                                  <strong>{item.numero || '—'}</strong>
                                  <span className="nh-email-batch-row-client">
                                    {item.societe || '—'}
                                  </span>
                                  <span className="nh-email-batch-row-meta">
                                    {item.designation || '—'}
                                    {' · '}
                                    {formatNhDisplayDate(item.date, item.numero)}
                                    {item.montantHT != null && item.montantHT !== ''
                                      ? ` · ${item.montantHT} HT`
                                      : ''}
                                  </span>
                                  <span
                                    className={`nh-email-batch-row-email${clientEmail ? '' : ' missing'
                                      }`}
                                  >
                                    {clientEmail || 'E-mail client manquant'}
                                  </span>
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </>
                    )}
                    {emailBatchResults && emailBatchResults.length > 0 && (
                      <div className="nh-email-batch-results">
                        <h4>Résultat de l&apos;envoi</h4>
                        <ul>
                          {emailBatchResults.map((r) => (
                            <li
                              key={r.label}
                              className={`nh-email-batch-result nh-email-batch-result--${r.status}`}
                            >
                              <strong>{r.label}</strong>
                              {r.status === 'sent' && (
                                <span> — envoyé à {r.message}</span>
                              )}
                              {r.status === 'skipped' && (
                                <span> — ignoré : {r.message}</span>
                              )}
                              {r.status === 'error' && (
                                <span> — erreur : {r.message}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn-cancel-edit"
                    onClick={closeEmailBatchModal}
                    disabled={emailBatchSending}
                  >
                    <i className="fas fa-times" aria-hidden="true" />
                    {emailBatchResults ? 'Fermer' : 'Annuler'}
                  </button>
                  <button
                    type="button"
                    className="btn-save-edit nh-email-batch-send-btn"
                    disabled={
                      emailBatchSending ||
                      emailBatchSelectedCount === 0 ||
                      overviewRowsFiltered.length === 0
                    }
                    onClick={handleConfirmEmailBatch}
                  >
                    <i className="fas fa-paper-plane" aria-hidden="true" />
                    {emailBatchSending
                      ? `Envoi en cours (${emailBatchSelectedCount})…`
                      : `Envoyer (${emailBatchSelectedCount})`}
                  </button>
                </div>
              </div>
            </div>
          )}

          {showModal && (
            <div className="modal-backdrop">
              <div className="modal-container">
                <div className="modal-header"><h3>Liste Des Sociétés</h3><button className="close-x" onClick={() => setShowModal(false)}>&times;</button></div>
                <div className="modal-body">
                  <div className="modal-search-bar">
                    <span className="results-count">
                      <strong>{filteredSocieties.length}</strong> Client(s)
                      {nhClientsLoading && <span> - Chargement...</span>}
                    </span>
                    <div className="search-input-wrapper">
                      <input type="text" placeholder="Chercher" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                      <button className="search-icon-btn"><i className="fas fa-search"></i></button>
                    </div>
                  </div>
                  {nhClientsError && (
                    <div style={{ color: '#b91c1c', marginBottom: '10px', fontSize: '0.9rem' }}>
                      {nhClientsError}
                    </div>
                  )}
                  <div className="table-wrapper" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    <table className="society-list-table">
                      <tbody>
                        {filteredSocieties.length > 0 ? filteredSocieties.map((s) => (
                          <tr key={s.id} onClick={() => handleSelectSociety(s)} className="selectable-row">
                            <td>{s.username || s.id}</td>
                            <td>{s.nom}</td>
                            <td>{s.matricule || '—'}</td>
                            <td>{s.personneType === 'PP' ? 'P.P' : s.personneType === 'PM' ? 'P.M' : '—'}</td>
                            <td>{s.nhType === 'NH' ? 'NH' : 'Passager'}</td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan="5" style={{ textAlign: 'center', color: '#6b7280' }}>
                              Aucun client trouvé
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn-close-modal" onClick={() => setShowModal(false)}>Fermer</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

}
